"use client";
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import React from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import Select from "react-select";
import AlertDialog from "./AlertDialog";
import { multiTokenSearch } from "./searchUtils";
import LaborVarianceModal from "./LaborVarianceModal";
import BatchRateChangeModal from "./BatchRateChangeModal";

const API_BASE = "https://erp-production-72da01c8e651.herokuapp.com/api";

const customStyles = {
  control: (provided, state) => ({
    ...provided,
    backgroundColor: "#374151",
    borderColor: state.isFocused ? "#f97316" : "#4b5563",
    color: "#e5e7eb",
    boxShadow: "none",
    outline: "none",
    "&:hover": { borderColor: "#f97316" },
    minHeight: "32px",
    fontSize: "12px",
  }),
  singleValue: (provided) => ({
    ...provided,
    color: "#e5e7eb",
    fontSize: "12px",
  }),
  placeholder: (provided) => ({
    ...provided,
    color: "#9ca3af",
    fontSize: "12px",
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? "#4b5563" : "#374151",
    color: state.isSelected ? "#f97316" : "#e5e7eb",
    "&:hover": { backgroundColor: "#4b5563" },
    fontSize: "12px",
  }),
  menu: (provided) => ({
    ...provided,
    backgroundColor: "#374151",
    borderRadius: "0.375rem",
    boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
  }),
};

const multiSelectFilterStyles = {
  control: (provided, state) => ({
    ...provided,
    backgroundColor: "#1f2937",
    borderColor: state.isFocused ? "#f97316" : "#4b5563",
    color: "#e5e7eb",
    boxShadow: "none",
    minHeight: "50px",
    zIndex: 100000,
    fontSize: "14px",
    "&:hover": { borderColor: "#f97316" },
  }),
  input: (provided) => ({ ...provided, color: "#e5e7eb" }),
  placeholder: (provided) => ({
    ...provided,
    color: "#9ca3af",
    fontSize: "14px",
  }),
  menuPortal: (base) => ({ ...base, zIndex: 999999 }),
  menu: (base) => ({
    ...base,
    backgroundColor: "#374151",
    borderRadius: "0.5rem",
    boxShadow: "0 10px 40px rgba(0, 0, 0, 0.5)",
    border: "1px solid #4b5563",
    width: "300px",
    maxWidth: "300px",
    minWidth: "260px",
    zIndex: 999999,
  }),
  menuList: (base) => ({
    ...base,
    maxHeight: "380px",
    padding: "8px",
    overflowY: "auto",
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isFocused ? "#4b5563" : "#374151",
    color: state.isSelected ? "#f97316" : "#e5e7eb",
    padding: "10px 12px",
    cursor: "pointer",
    fontSize: "13.5px",
    whiteSpace: "normal",
    wordBreak: "break-word",
    lineHeight: "1.4",
    "&:hover": { backgroundColor: "#4b5563" },
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: "4px 8px",
    display: "flex",
    flexWrap: "wrap",
    gap: "6px",
    alignItems: "flex-start",
  }),
  multiValue: (base) => ({
    ...base,
    backgroundColor: "#4b5563",
    borderRadius: "6px",
    margin: 0,
    fontSize: "13px",
  }),
  multiValueLabel: (base) => ({
    ...base,
    color: "#e5e7eb",
    padding: "3px 8px",
    paddingRight: "6px",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    maxWidth: "150px",
  }),
  multiValueRemove: (base) => ({
    ...base,
    color: "#9ca3af",
    cursor: "pointer",
    paddingLeft: "4px",
    paddingRight: "4px",
    borderRadius: "4px",
    ":hover": { backgroundColor: "#f97316", color: "white" },
  }),
};

// ---------------------------------------------------------------------------
// AddItemsModal — unchanged from original
// ---------------------------------------------------------------------------
function AddItemsModal({
  isOpen,
  onClose,
  onAdd,
  availableItems,
  existingItemIds,
}) {
  const [selectedItemIds, setSelectedItemIds] = useState(new Set());
  const [filters, setFilters] = useState({
    conditionName: [],
    sectionNumber: [],
    bidArea: [],
    uom: [],
    descriptionSearch: "",
  });
  const [showFilter, setShowFilter] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const searchTimeoutRef = useRef(null);
  const conditionRef = useRef(null);
  const sectionRef = useRef(null);
  const bidAreaRef = useRef(null);
  const uomRef = useRef(null);

  const getItemKey = useCallback(
    (item) => `${String(item.assemblyId)}-${String(item._id)}`,
    [],
  );

  useEffect(() => {
    if (isOpen) {
      setSelectedItemIds(new Set());
      setFilters({
        conditionName: [],
        sectionNumber: [],
        bidArea: [],
        uom: [],
        descriptionSearch: "",
      });
      setShowFilter(null);
      setCurrentPage(1);
      setSearchInput("");
    }
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!showFilter) return;
      const refs = {
        conditionName: conditionRef,
        sectionNumber: sectionRef,
        bidArea: bidAreaRef,
        uom: uomRef,
      };
      const activeRef = refs[showFilter];
      if (activeRef?.current && activeRef.current.contains(event.target))
        return;
      const clickedInsideReactSelect =
        event.target.closest('[class*="css-"]') ||
        event.target.closest(".react-select__menu") ||
        event.target.closest(".react-select__menu-portal") ||
        event.target.closest(".react-select__option") ||
        (event.target.id && event.target.id.startsWith("react-select"));
      if (clickedInsideReactSelect) return;
      setShowFilter(null);
    };
    document.addEventListener("mousedown", handleClickOutside, true);
    return () =>
      document.removeEventListener("mousedown", handleClickOutside, true);
  }, [showFilter]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filters]);

  const unassignedItems = useMemo(
    () =>
      availableItems.filter(
        (item) =>
          !existingItemIds.has(
            `${String(item.assemblyId)}-${String(item._id)}`,
          ),
      ),
    [availableItems, existingItemIds],
  );

  const filteredItems = useMemo(() => {
    let results = unassignedItems.filter((item) => {
      if (
        filters.conditionName.length > 0 &&
        !filters.conditionName.includes(item.conditionName)
      )
        return false;
      if (
        filters.sectionNumber.length > 0 &&
        !filters.sectionNumber.includes(item.sectionNumber)
      )
        return false;
      if (filters.bidArea.length > 0 && !filters.bidArea.includes(item.bidArea))
        return false;
      if (filters.uom.length > 0 && !filters.uom.includes(item.uom))
        return false;
      return true;
    });

    if (filters.descriptionSearch) {
      // Build a shape multiTokenSearch can work with
      const shaped = results.map((item) => ({
        ...item,
        // multiTokenSearch uses item.description — map the right fields
        description: item.costCodeDescription || item.itemDescription || "",
        sectionNumber: item.sectionNumber || "",
        uom: item.uom || "",
        _searchBlob: undefined, // let it build fresh
      }));
      const matched = multiTokenSearch(shaped, filters.descriptionSearch);
      // matched items carry their original data, just return them
      results = matched;
    }

    return results;
  }, [unassignedItems, filters]);

  const sortedFilteredItems = useMemo(() => {
    return [...filteredItems].sort((a, b) => {
      const assemblyA = (a.conditionName || "Uncategorized").trim();
      const assemblyB = (b.conditionName || "Uncategorized").trim();
      if (assemblyA !== assemblyB)
        return assemblyA.localeCompare(assemblyB, undefined, {
          numeric: true,
          sensitivity: "base",
        });
      const sectionA = a.sectionNumber || "";
      const sectionB = b.sectionNumber || "";
      if (sectionA !== sectionB)
        return sectionA.localeCompare(sectionB, undefined, {
          numeric: true,
          sensitivity: "base",
        });
      const descA = (
        a.costCodeDescription ||
        a.itemDescription ||
        ""
      ).toLowerCase();
      const descB = (
        b.costCodeDescription ||
        b.itemDescription ||
        ""
      ).toLowerCase();
      if (descA !== descB) return descA.localeCompare(descB);
      return (a.bidArea || "").localeCompare(b.bidArea || "");
    });
  }, [filteredItems]);

  const paginatedItems = useMemo(() => {
    const start = (currentPage - 1) * 100;
    return sortedFilteredItems.slice(start, start + 100);
  }, [sortedFilteredItems, currentPage]);

  const totalPages = Math.ceil(sortedFilteredItems.length / 100);

  const groupedItems = useMemo(() => {
    const groups = {};
    paginatedItems.forEach((item) => {
      const key = item.conditionName || "Uncategorized";
      if (!groups[key]) groups[key] = [];
      groups[key].push(item);
    });
    return groups;
  }, [paginatedItems]);

  const selectedItems = useMemo(
    () => filteredItems.filter((item) => selectedItemIds.has(getItemKey(item))),
    [filteredItems, selectedItemIds, getItemKey],
  );

  const totals = useMemo(
    () =>
      selectedItems.reduce(
        (acc, item) => ({
          count: acc.count + 1,
          hours: acc.hours + (item.laborHours || 0),
          amount: acc.amount + (item.amount || 0),
        }),
        { count: 0, hours: 0, amount: 0 },
      ),
    [selectedItems],
  );

  const filteredTotals = useMemo(
    () =>
      filteredItems.reduce(
        (acc, item) => ({
          count: acc.count + 1,
          hours: acc.hours + (item.laborHours || 0),
          amount: acc.amount + (item.amount || 0),
        }),
        { count: 0, hours: 0, amount: 0 },
      ),
    [filteredItems],
  );

  const filterOptions = useMemo(
    () => ({
      conditionName: [
        ...new Set(unassignedItems.map((i) => i.conditionName).filter(Boolean)),
      ].sort((a, b) =>
        a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" }),
      ),
      sectionNumber: [
        ...new Set(unassignedItems.map((i) => i.sectionNumber).filter(Boolean)),
      ].sort((a, b) =>
        a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" }),
      ),
      bidArea: [
        ...new Set(unassignedItems.map((i) => i.bidArea).filter(Boolean)),
      ].sort(),
      uom: [
        ...new Set(unassignedItems.map((i) => i.uom).filter(Boolean)),
      ].sort(),
    }),
    [unassignedItems],
  );

  const hasActiveFilters = Object.values(filters).some((f) =>
    Array.isArray(f) ? f.length > 0 : f,
  );
  const toggleFilter = (field) =>
    setShowFilter((prev) => (prev === field ? null : field));

  const handleFilterChange = (type, value) => {
    if (type === "descriptionSearch") {
      setFilters((prev) => ({ ...prev, descriptionSearch: value }));
    } else {
      setFilters((prev) => ({ ...prev, [type]: value || [] }));
    }
  };

  const handleDescriptionSearchChange = (e) => {
    const value = e.target.value;
    setSearchInput(value);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(
      () => handleFilterChange("descriptionSearch", value),
      300,
    );
  };

  const clearAllFilters = () => {
    setFilters({
      conditionName: [],
      sectionNumber: [],
      bidArea: [],
      uom: [],
      descriptionSearch: "",
    });
    setSearchInput("");
    setSelectedItemIds(new Set());
    setCurrentPage(1);
  };

  const handleSelectAllFiltered = () => {
    if (selectedItemIds.size === filteredItems.length) {
      setSelectedItemIds(new Set());
    } else {
      setSelectedItemIds(new Set(filteredItems.map((i) => getItemKey(i))));
    }
  };

  const allFilteredSelected =
    selectedItemIds.size === filteredItems.length && filteredItems.length > 0;

  const handleItemSelect = (item) => {
    setSelectedItemIds((prev) => {
      const newSet = new Set(prev);
      const key = getItemKey(item);
      if (newSet.has(key)) newSet.delete(key);
      else newSet.add(key);
      return newSet;
    });
  };

  const formatCurrency = (num) =>
    `$${Number(num || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const handleAddSelected = () => {
    const itemsToAdd = selectedItems.map((item) => ({
      assemblyId: String(item.assemblyId),
      lineItemId: String(item._id),
    }));
    onAdd(itemsToAdd);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
        <div
          className="fixed inset-0 transition-opacity bg-gray-900 bg-opacity-75"
          onClick={onClose}
        />
        <div className="relative inline-block w-full max-w-7xl px-4 pt-5 pb-4 overflow-hidden text-left align-bottom transition-all transform bg-gray-800 rounded-lg shadow-xl sm:my-8 sm:align-middle sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-xl font-semibold text-white">
                Add Labor Items
              </h3>
              <p className="text-sm text-gray-400 mt-1">
                {totals.count} items selected • {formatCurrency(totals.amount)}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white text-2xl leading-none"
            >
              ×
            </button>
          </div>

          <div className="bg-gray-900 rounded-lg p-3 mb-3">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-4 text-xl">
                <span className="text-gray-400">
                  <span className="text-white font-semibold">
                    {filteredItems.length.toLocaleString()}
                  </span>
                  {filteredItems.length !== unassignedItems.length && (
                    <span className="text-gray-500">
                      {" "}
                      / {unassignedItems.length.toLocaleString()}
                    </span>
                  )}{" "}
                  available
                </span>
                <span className="text-gray-600">|</span>
                <span className="text-orange-400 font-medium">
                  {formatCurrency(filteredTotals.amount)}
                </span>
                <span className="text-gray-600">|</span>
                <span className="text-blue-400">
                  {filteredTotals.hours.toFixed(1)} hrs
                </span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleSelectAllFiltered}
                  className="px-3 py-1.5 text-xs font-medium text-white bg-orange-500 rounded hover:bg-orange-600"
                >
                  {allFilteredSelected ? "Deselect All" : "Select All"}
                </button>
                <span className="text-gray-400 text-sm">
                  {selectedItems.length} selected
                </span>
              </div>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              <div className="relative">
                <input
                  type="text"
                  value={searchInput}
                  onChange={handleDescriptionSearchChange}
                  placeholder="Search..."
                  className="w-40 px-2 py-1 text-xs bg-gray-700 border border-gray-600 rounded text-gray-200 placeholder-gray-500 focus:border-orange-500 focus:outline-none"
                />
                {searchInput && (
                  <button
                    type="button"
                    onClick={() => {
                      setSearchInput("");
                      handleFilterChange("descriptionSearch", "");
                    }}
                    className="absolute right-1 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                  >
                    ×
                  </button>
                )}
              </div>
              <span className="text-gray-600">|</span>
              {filters.conditionName.length > 0 && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[11px] font-medium bg-teal-600/80 text-white rounded">
                  Condition:{" "}
                  {filters.conditionName.length === 1
                    ? filters.conditionName[0].substring(0, 15) +
                      (filters.conditionName[0].length > 15 ? "…" : "")
                    : `${filters.conditionName.length} selected`}
                  <button
                    type="button"
                    onClick={() => handleFilterChange("conditionName", [])}
                    className="hover:text-teal-200"
                  >
                    ×
                  </button>
                </span>
              )}
              {filters.sectionNumber.length > 0 && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[11px] font-medium bg-purple-600/80 text-white rounded">
                  Section:{" "}
                  {filters.sectionNumber.length === 1
                    ? filters.sectionNumber[0]
                    : `${filters.sectionNumber.length} selected`}
                  <button
                    type="button"
                    onClick={() => handleFilterChange("sectionNumber", [])}
                    className="hover:text-purple-200"
                  >
                    ×
                  </button>
                </span>
              )}
              {filters.bidArea.length > 0 && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[11px] font-medium bg-green-600/80 text-white rounded">
                  Bid Area:{" "}
                  {filters.bidArea.length === 1
                    ? filters.bidArea[0]
                    : `${filters.bidArea.length} selected`}
                  <button
                    type="button"
                    onClick={() => handleFilterChange("bidArea", [])}
                    className="hover:text-green-200"
                  >
                    ×
                  </button>
                </span>
              )}
              {filters.uom.length > 0 && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[11px] font-medium bg-indigo-600/80 text-white rounded">
                  UOM:{" "}
                  {filters.uom.length === 1
                    ? filters.uom[0]
                    : `${filters.uom.length} selected`}
                  <button
                    type="button"
                    onClick={() => handleFilterChange("uom", [])}
                    className="hover:text-indigo-200"
                  >
                    ×
                  </button>
                </span>
              )}
              {hasActiveFilters && (
                <button
                  type="button"
                  onClick={clearAllFilters}
                  className="px-1.5 py-0.5 text-[11px] font-medium text-red-400 hover:text-red-300 hover:bg-red-900/30 rounded"
                >
                  Clear all
                </button>
              )}
            </div>
          </div>

          <div className="overflow-x-auto rounded-lg border border-gray-700 max-h-[50vh]">
            <table className="w-full text-[13px]">
              <thead className="bg-gray-800/95 sticky top-0 z-10">
                <tr>
                  <th className="w-8 px-2 py-2 text-center text-xs font-medium text-gray-400 uppercase">
                    <input
                      type="checkbox"
                      checked={allFilteredSelected}
                      onChange={handleSelectAllFiltered}
                      className="h-4 w-4 text-orange-400 rounded border-gray-500"
                    />
                  </th>
                  {[
                    {
                      field: "conditionName",
                      ref: conditionRef,
                      label: "Condition",
                      width: "300px",
                    },
                    {
                      field: "sectionNumber",
                      ref: sectionRef,
                      label: "Section",
                      width: "200px",
                    },
                  ].map(({ field, ref: colRef, label, width }) => (
                    <th
                      key={field}
                      className={`px-2 py-2 text-left text-xs font-medium cursor-pointer relative hover:text-orange-300 ${filters[field].length > 0 ? "text-orange-400" : "text-gray-400"} uppercase`}
                      onClick={() => toggleFilter(field)}
                    >
                      <span className="flex items-center gap-0.5">
                        {label}
                        <svg
                          className="w-3 h-3"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                        {filters[field].length > 0 && (
                          <span className="ml-0.5 px-1 py-0.5 text-[9px] bg-orange-500 text-white rounded-full">
                            {filters[field].length}
                          </span>
                        )}
                      </span>
                      {showFilter === field && (
                        <div
                          ref={colRef}
                          className="absolute left-0 top-full z-[99999] mt-1 bg-gray-700 border border-gray-600 rounded-lg shadow-2xl"
                          style={{ minWidth: width }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="p-3">
                            <Select
                              isMulti
                              closeMenuOnSelect={false}
                              hideSelectedOptions={false}
                              options={filterOptions[field].map((v) => ({
                                value: v,
                                label: v,
                              }))}
                              value={filterOptions[field]
                                .map((v) => ({ value: v, label: v }))
                                .filter((opt) =>
                                  filters[field].includes(opt.value),
                                )}
                              onChange={(opts) =>
                                handleFilterChange(
                                  field,
                                  opts ? opts.map((o) => o.value) : [],
                                )
                              }
                              menuPortalTarget={document.body}
                              menuPosition="fixed"
                              styles={multiSelectFilterStyles}
                            />
                          </div>
                        </div>
                      )}
                    </th>
                  ))}
                  <th className="px-2 py-2 text-left text-xs font-medium text-gray-400 uppercase">
                    Description
                  </th>
                  <th
                    className={`px-2 py-2 text-left text-xs font-medium cursor-pointer relative hover:text-orange-300 ${filters.bidArea.length > 0 ? "text-orange-400" : "text-gray-400"} uppercase`}
                    onClick={() => toggleFilter("bidArea")}
                  >
                    <span className="flex items-center gap-0.5">
                      Bid Area
                      <svg
                        className="w-3 h-3"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                      {filters.bidArea.length > 0 && (
                        <span className="ml-0.5 px-1 py-0.5 text-[9px] bg-orange-500 text-white rounded-full">
                          {filters.bidArea.length}
                        </span>
                      )}
                    </span>
                    {showFilter === "bidArea" && (
                      <div
                        ref={bidAreaRef}
                        className="absolute left-0 top-full z-[99999] mt-1 bg-gray-700 border border-gray-600 rounded-lg shadow-2xl"
                        style={{ minWidth: "280px" }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="p-3">
                          <Select
                            isMulti
                            closeMenuOnSelect={false}
                            hideSelectedOptions={false}
                            options={filterOptions.bidArea.map((v) => ({
                              value: v,
                              label: v,
                            }))}
                            value={filterOptions.bidArea
                              .map((v) => ({ value: v, label: v }))
                              .filter((opt) =>
                                filters.bidArea.includes(opt.value),
                              )}
                            onChange={(opts) =>
                              handleFilterChange(
                                "bidArea",
                                opts ? opts.map((o) => o.value) : [],
                              )
                            }
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            styles={multiSelectFilterStyles}
                          />
                        </div>
                      </div>
                    )}
                  </th>
                  <th className="px-2 py-2 text-right text-xs font-medium text-gray-400 uppercase">
                    Qty
                  </th>
                  <th
                    className={`px-2 py-2 text-left text-xs font-medium cursor-pointer relative hover:text-orange-300 ${filters.uom.length > 0 ? "text-orange-400" : "text-gray-400"} uppercase`}
                    onClick={() => toggleFilter("uom")}
                  >
                    <span className="flex items-center gap-0.5">
                      UOM
                      <svg
                        className="w-3 h-3"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                      {filters.uom.length > 0 && (
                        <span className="ml-0.5 px-1 py-0.5 text-[9px] bg-orange-500 text-white rounded-full">
                          {filters.uom.length}
                        </span>
                      )}
                    </span>
                    {showFilter === "uom" && (
                      <div
                        ref={uomRef}
                        className="absolute left-0 top-full z-[99999] mt-1 bg-gray-700 border border-gray-600 rounded-lg shadow-2xl"
                        style={{ minWidth: "180px" }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="p-3">
                          <Select
                            isMulti
                            closeMenuOnSelect={false}
                            hideSelectedOptions={false}
                            options={filterOptions.uom.map((v) => ({
                              value: v,
                              label: v,
                            }))}
                            value={filterOptions.uom
                              .map((v) => ({ value: v, label: v }))
                              .filter((opt) => filters.uom.includes(opt.value))}
                            onChange={(opts) =>
                              handleFilterChange(
                                "uom",
                                opts ? opts.map((o) => o.value) : [],
                              )
                            }
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            styles={multiSelectFilterStyles}
                          />
                        </div>
                      </div>
                    )}
                  </th>
                  <th className="px-2 py-2 text-right text-xs font-medium text-gray-400 uppercase">
                    Hours
                  </th>
                  <th className="px-2 py-2 text-right text-xs font-medium text-gray-400 uppercase">
                    Amount
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700/50">
                {Object.entries(groupedItems).map(([condition, items]) => (
                  <React.Fragment key={condition}>
                    <tr className="bg-gray-800/80">
                      <td
                        colSpan="9"
                        className="px-4 py-2 text-sm font-medium text-white"
                      >
                        <span className="flex items-center justify-between">
                          <span>{condition}</span>
                          <span className="text-gray-400 text-xs">
                            {items.length} items
                          </span>
                        </span>
                      </td>
                    </tr>
                    {items.map((item, idx) => {
                      const itemKey = getItemKey(item);
                      const isSelected = selectedItemIds.has(itemKey);
                      return (
                        <tr
                          key={itemKey}
                          className={`hover:bg-gray-700/50 cursor-pointer ${idx % 2 === 0 ? "bg-gray-800/30" : "bg-gray-400/5"} ${isSelected ? "ring-1 ring-orange-500/50 bg-orange-900/20" : ""}`}
                          onClick={() => handleItemSelect(item)}
                        >
                          <td
                            className="px-2 py-2 text-center"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => handleItemSelect(item)}
                              className="h-4 w-4 text-orange-400 rounded border-gray-500"
                            />
                          </td>
                          <td className="px-2 py-2 text-sm text-gray-300">
                            {item.conditionName || "-"}
                          </td>
                          <td className="px-2 py-2 text-sm text-white">
                            {item.sectionNumber || "-"}
                          </td>
                          <td
                            className="px-2 py-2 text-sm text-gray-200 max-w-[220px] truncate"
                            title={
                              item.costCodeDescription || item.itemDescription
                            }
                          >
                            {item.costCodeDescription ||
                              item.itemDescription ||
                              "No Description"}
                          </td>
                          <td className="px-2 py-2 text-sm text-gray-200">
                            {item.bidArea || "-"}
                          </td>
                          <td className="px-2 py-2 text-sm text-right text-white font-medium">
                            {Number(item.quantity || 0).toLocaleString(
                              "en-US",
                              {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              },
                            )}
                          </td>
                          <td className="px-2 py-2 text-sm text-gray-200">
                            {item.uom || "-"}
                          </td>
                          <td className="px-2 py-2 text-sm text-right text-orange-400 font-medium">
                            {item.laborHours || 0}
                          </td>
                          <td className="px-2 py-2 text-sm text-right text-white">{`$${Number(item.amount || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}</td>
                        </tr>
                      );
                    })}
                  </React.Fragment>
                ))}
                {filteredItems.length === 0 && (
                  <tr>
                    <td
                      colSpan="9"
                      className="px-4 py-12 text-center text-gray-400"
                    >
                      No items matching filters
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 py-4 mt-2">
              <button
                type="button"
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                className="px-2 py-1 text-[13px] bg-gray-700 text-gray-400 rounded disabled:opacity-30 hover:bg-gray-600"
              >
                ««
              </button>
              <button
                type="button"
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="px-2 py-1 text-[13px] bg-gray-700 text-white rounded disabled:opacity-30 hover:bg-gray-600"
              >
                ‹ Prev
              </button>
              <span className="text-[13px] text-white">
                Page {currentPage} of {totalPages}
              </span>
              <button
                type="button"
                onClick={() =>
                  setCurrentPage((p) => Math.min(totalPages, p + 1))
                }
                disabled={currentPage === totalPages}
                className="px-2 py-1 text-[13px] bg-gray-700 text-white rounded disabled:opacity-30 hover:bg-gray-600"
              >
                Next ›
              </button>
              <button
                type="button"
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
                className="px-2 py-1 text-[13px] bg-gray-700 text-gray-400 rounded disabled:opacity-30 hover:bg-gray-600"
              >
                »»
              </button>
            </div>
          )}

          {totals.count > 0 && (
            <div className="mt-4 p-4 bg-gray-900 rounded-lg">
              <div className="grid grid-cols-3 gap-4 text-gray-200">
                <div>
                  <span className="text-gray-400">Items:</span> {totals.count}
                </div>
                <div>
                  <span className="text-gray-400">Total Hours:</span>{" "}
                  {totals.hours.toFixed(1)}
                </div>
                <div>
                  <span className="text-gray-400">Estimated Total:</span>{" "}
                  <span className="text-orange-400 font-semibold">{`$${Number(totals.amount || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}</span>
                </div>
              </div>
            </div>
          )}

          <div className="mt-6 flex justify-end space-x-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-200 bg-gray-600 rounded-md hover:bg-gray-500"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleAddSelected}
              disabled={totals.count === 0}
              className={`px-4 py-2 text-sm text-white rounded-md ${totals.count === 0 ? "bg-orange-300 cursor-not-allowed" : "bg-orange-500 hover:bg-orange-600"}`}
            >
              Add {totals.count} Item{totals.count !== 1 ? "s" : ""}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// calculateTimesheetSummary removed — backend /timesheet-summary endpoint
// returns pre-aggregated { approved: {...}, pending: {...} } directly.

// ---------------------------------------------------------------------------
// WorkOrderViewNew — main component
// ---------------------------------------------------------------------------
export default function WorkOrderViewNew() {
  const { id, woId } = useParams();
  const navigate = useNavigate();
  const [selectedRowKeys, setSelectedRowKeys] = useState(new Set());
  // { isOpen, items, mode }  — items are the raw row objects to send to the modal
  const [batchRateModal, setBatchRateModal] = useState({
    isOpen: false,
    items: [],
    mode: "selection",
  });
  const [editedContractLabourItem, setEditedContractLabourItem] =
    useState(null);
  const [contractLabourOptions, setContractLabourOptions] = useState([]);
  const [groupMainTable, setGroupMainTable] = useState(false);
  const [wo, setWO] = useState(null);
  const [woItems, setWoItems] = useState([]);
  const [availableItems, setAvailableItems] = useState([]);
  const [costCodes, setCostCodes] = useState([]);
  const [editing, setEditing] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [timesheetSummary, setTimesheetSummary] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [pieceworkLogCount, setPieceworkLogCount] = useState(0);
  const [varianceItem, setVarianceItem] = useState(null); // item row being edited
  // ── Progress log state ──
  const [progressRawData, setProgressRawData] = useState(null);
  // shape: { approvedMap: {lineItemId: qty}, pendingMap: {lineItemId: qty}, logCount: number }

  const [alert, setAlert] = useState({
    open: false,
    type: "",
    title: "",
    message: "",
  });

  const [editedTitle, setEditedTitle] = useState("");
  const [editedCostCode, setEditedCostCode] = useState("");
  const [editedNotes, setEditedNotes] = useState("");
  const [editedType, setEditedType] = useState("");
  const [itemsToAdd, setItemsToAdd] = useState([]);
  const [itemsToRemove, setItemsToRemove] = useState([]);

  const [woTableFilters, setWoTableFilters] = useState({
    conditionName: [],
    sectionNumber: [],
    bidArea: [],
    uom: [],
    descriptionSearch: "",
  });
  const [showWoTableFilter, setShowWoTableFilter] = useState(null);
  const [pieceworkerBurdenPercent, setPieceworkerBurdenPercent] = useState(0);

  const woTableConditionRef = useRef(null);
  const woTableSectionRef = useRef(null);
  const woTableBidAreaRef = useRef(null);
  const woTableUomRef = useRef(null);

  const [unions, setUnions] = useState([]);
  const [editedUnion, setEditedUnion] = useState("");

  const burdenFactor = useMemo(
    () => 1 + pieceworkerBurdenPercent / 100,
    [pieceworkerBurdenPercent],
  );
  const foremanRate = (unitRate) => {
    if (burdenFactor <= 1 || !unitRate) return Number(unitRate || 0);
    return Number((Number(unitRate) / burdenFactor).toFixed(2));
  };

  const formatCurrency = (num) =>
    `$${Number(num || 0).toLocaleString("en-US", { minimumFractionDigits: 2 })}`;

  const formatQty = (num, decimals = 2) => {
    const n = Number(num || 0);
    return n.toLocaleString("en-US", {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
  };

  // ── Click-outside for WO table filters ──
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!showWoTableFilter) return;
      const refs = {
        conditionName: woTableConditionRef,
        sectionNumber: woTableSectionRef,
        bidArea: woTableBidAreaRef,
        uom: woTableUomRef,
      };
      const activeRef = refs[showWoTableFilter];
      if (activeRef?.current?.contains(event.target)) return;
      const isReactSelectClick =
        event.target.closest('[class*="css-"]') ||
        event.target.closest(".react-select__menu") ||
        event.target.closest(".react-select__menu-portal") ||
        event.target.closest(".react-select__option") ||
        event.target.id?.startsWith("react-select");
      if (isReactSelectClick) return;
      setShowWoTableFilter(null);
    };
    document.addEventListener("mousedown", handleClickOutside, true);
    return () =>
      document.removeEventListener("mousedown", handleClickOutside, true);
  }, [showWoTableFilter]);

  // ── Piecework summary ──
  const pieceworkSummary = useMemo(() => {
    if (wo?.type !== "piecework") return null;
    const active = woItems.filter((i) => !i.closedOutBySizeChange);
    return {
      estimatedTotal: active.reduce((s, i) => s + (i.amount || 0), 0),
      chargedAmount: active.reduce(
        (s, i) => s + (i.qtyOrdered || i.qtyCharged || 0) * (i.unitRate || 0),
        0,
      ),
      pendingAmount: active.reduce(
        (s, i) => s + (i.qtyPending || 0) * (i.unitRate || 0),
        0,
      ),
      remainingAmount: active.reduce(
        (s, i) => s + (i.netRemaining || 0) * (i.unitRate || 0),
        0,
      ),
      totalQtyCharged: active.reduce(
        (s, i) => s + (i.qtyOrdered || i.qtyCharged || 0),
        0,
      ),
      totalQtyPending: active.reduce((s, i) => s + (i.qtyPending || 0), 0),
      totalQtyRemaining: active.reduce((s, i) => s + (i.netRemaining || 0), 0),
      logCount: pieceworkLogCount,
    };
  }, [wo?.type, woItems, pieceworkLogCount]);

  // ── Progress stats memo — joins raw log maps against woItems ──
  const progressStats = useMemo(() => {
    if (wo?.type !== "hourly" || !progressRawData) return null;
    const { approvedMap, pendingMap, logCount } = progressRawData;
    const activeItems = woItems.filter((i) => !i.closedOutBySizeChange);

    let totalEstimated = 0;
    let totalApproved = 0;
    let totalPending = 0;
    let totalApprovedCost = 0;
    let totalPendingCost = 0;

    const byItem = {};

    activeItems.forEach((item) => {
      const key = String(item.lineItemId || item._id || "");
      const estimated = item.netRequiredQty ?? item.quantity ?? 0;
      const approved = approvedMap[key] || 0;
      const pending = pendingMap[key] || 0;
      const rate = item.unitRate || 0;

      totalEstimated += estimated;
      totalApproved += approved;
      totalPending += pending;
      totalApprovedCost += approved * rate;
      totalPendingCost += pending * rate;

      byItem[key] = { estimated, approved, pending, rate };
    });

    const completionPct =
      totalEstimated > 0
        ? Math.min(100, (totalApproved / totalEstimated) * 100)
        : 0;
    const pendingPct =
      totalEstimated > 0
        ? Math.min(100 - completionPct, (totalPending / totalEstimated) * 100)
        : 0;
    const estimatedCost = activeItems.reduce((s, i) => s + (i.amount || 0), 0);

    const totalRemainingCost = Math.max(
      0,
      estimatedCost - totalApprovedCost - totalPendingCost,
    );

    return {
      totalEstimated,
      totalApproved,
      totalPending,
      totalRemaining: Math.max(
        0,
        totalEstimated - totalApproved - totalPending,
      ),
      totalApprovedCost,
      totalPendingCost,
      totalRemainingCost,
      estimatedCost,
      completionPct,
      pendingPct,
      logCount,
      byItem,
    };
  }, [wo?.type, progressRawData, woItems]);

  const canChangeType = useMemo(() => {
    if (!wo) return false;
    const hasAnyCharges = woItems.some(
      (item) =>
        (item.qtyCharged || 0) > 0 ||
        (item.laborHoursCharged || 0) > 0 ||
        (item.qtyPending || 0) > 0,
    );
    if (wo.type === "hourly" && timesheetSummary)
      return (timesheetSummary.approved?.hours || 0) <= 0.01 && !hasAnyCharges;
    return !hasAnyCharges;
  }, [wo, woItems, timesheetSummary]);

  // ── Fetch piecework logs ──
  useEffect(() => {
    if (wo?.type !== "piecework") {
      setPieceworkLogCount(0);
      return;
    }
    const fetchPieceworkLogs = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get(
          `${API_BASE}/pieceworkerLogs2?workOrder=${woId}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        const logs = Array.isArray(res.data) ? res.data : res.data?.data || [];
        setPieceworkLogCount(logs.length);
      } catch (err) {
        console.error("Failed to fetch piecework logs:", err);
        setPieceworkLogCount(0);
      }
    };
    fetchPieceworkLogs();
  }, [wo?.type, woId]);

  // ── Fetch progress logs for hourly WOs ──
  useEffect(() => {
    if (wo?.type !== "hourly") {
      setProgressRawData(null);
      return;
    }
    const fetchProgressLogs = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get(
          `${API_BASE}/progressLogs?workOrder=${woId}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        const logs = Array.isArray(res.data) ? res.data : res.data?.data || [];

        const approvedMap = {};
        const pendingMap = {};
        logs.forEach((log) => {
          if (!["approved", "pending"].includes(log.status)) return;
          const target = log.status === "approved" ? approvedMap : pendingMap;
          (log.lineItems || []).forEach((li) => {
            const key = String(li.lineItemId?.$oid || li.lineItemId);
            target[key] = (target[key] || 0) + (li.qtyInstalled || 0);
          });
        });

        setProgressRawData({ approvedMap, pendingMap, logCount: logs.length });
      } catch (err) {
        console.error("Failed to fetch progress logs:", err);
        setProgressRawData(null);
      }
    };
    fetchProgressLogs();
  }, [wo?.type, woId]);

  // ── Main data fetch ──
  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const token = localStorage.getItem("token");
        if (!token) throw new Error("No authentication token found");
        const headers = { Authorization: `Bearer ${token}` };

        const woRes = await axios.get(`${API_BASE}/workorders2/${woId}`, {
          headers,
        });
        const workOrderData = woRes.data;
        setWO(workOrderData);

        const costRes = await axios.get(`${API_BASE}/costCodes`, { headers });
        setCostCodes(
          costRes.data.map((c) => ({
            value: c._id,
            label: `${c.code} - ${c.description || "No description"}`,
          })),
        );

        const unionRes = await axios.get(`${API_BASE}/company-profile/unions`, {
          headers,
        });
        setUnions(unionRes.data);

        const netRes = await axios.get(
          `${API_BASE}/net-assemblies/project/${id}`,
          { headers, params: { category: "L" } },
        );
        const allNetLabor = netRes.data.data || [];
        const itemsForThisWO = allNetLabor.filter(
          (item) => item.linkedWO?.toString() === woId,
        );
        const mappedItems = itemsForThisWO.map((item) => ({
          ...item,
          assemblyId: item.assemblyId,
          lineItemId: item.lineItemId || item.key || item._id,
          description: item.costCodeDescription || item.itemDescription || "",
          quantity: item.netRequiredQty || 0,
          remaining: item.netRemaining || 0,
          qtyCharged: item.qtyOrdered || item.qtyCharged || 0,
          qtyPending: item.qtyPending || 0,
          laborHours: item.laborHours || item.netLaborHours || 0,
          laborHoursCharged: item.laborHoursCharged || 0,
          laborHoursRemaining: item.laborHoursRemaining || 0,
          isNewSpec: item.isNewSpec || false,
          closedOutBySizeChange: item.closedOutBySizeChange || false,
          sizeChangedFrom: item.sizeChangedFrom || null,
          hasVariance: item.hasVariance,
          deltaQty: item.deltaQty || 0,
          baseRequiredQty: item.baseRequiredQty || 0,
          originalQuantity: item.baseRequiredQty || item.originalQuantity,
          uom: item.uom || "-",
          bidArea: item.bidArea || "-",
          sectionNumber: item.sectionNumber || "-",
          conditionName: item.conditionName || "-",
          unitRate: item.unitRate || item.price || 0,
          amount: item.amount || 0,
        }));
        setWoItems(mappedItems);

        const contractRes = await axios.get(
          `${API_BASE}/projects/${id}/contract-labour-items`,
          { headers },
        );
        const options = (contractRes.data || []).map((item) => ({
          value: item._id,
          label: `${item.labourItem?.title || item.title || "No title"} - $${(item.unitCost || 0).toFixed(2)} / ${item.uom || "?"}`,
          details: item,
        }));
        setContractLabourOptions(options);

        setEditedTitle(workOrderData.title || "");
        setEditedCostCode(
          workOrderData.costCode?._id || workOrderData.costCode || "",
        );
        setEditedNotes(workOrderData.notes || "");
        setEditedType(workOrderData.type || "hourly");
        setEditedUnion(workOrderData.union || "non-union");

        const existingId =
          workOrderData.selectedContractLabourItem?._id ||
          workOrderData.selectedContractLabourItem ||
          null;
        const selectedOption = existingId
          ? options.find((opt) => opt.value === existingId)
          : null;
        setEditedContractLabourItem(selectedOption || null);

        // Persist the exact totals the detail view is showing so the project
        // list view matches. Uses the same filter the header card uses:
        // active (non-closed) items, sum of amount.
        try {
          const activeItems = mappedItems.filter(
            (i) => !i.closedOutBySizeChange,
          );
          const estimatedTotal = activeItems.reduce(
            (s, i) => s + (i.amount || 0),
            0,
          );
          const itemCount = activeItems.length;

          await axios.post(
            `${API_BASE}/workorders2/${woId}/save-totals`,
            { itemCount, estimatedTotal },
            { headers },
          );

          // Patch local WO state so the header card reflects saved values immediately
          setWO((prev) =>
            prev ? { ...prev, itemCount, estimatedTotal } : prev,
          );
        } catch (saveErr) {
          console.warn("WO totals save failed (non-fatal):", saveErr.message);
        }
      } catch (err) {
        console.error("Error loading work order data:", err);
        setAlert({
          open: true,
          type: "error",
          title: "Load Error",
          message:
            err.response?.data?.message ||
            err.message ||
            "Failed to load work order data",
        });
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [woId, id]);

  const refetchItems = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };
      const netRes = await axios.get(
        `${API_BASE}/net-assemblies/project/${id}`,
        {
          headers,
          params: { category: "L" },
        },
      );
      const allNetLabor = netRes.data.data || [];
      const itemsForThisWO = allNetLabor.filter(
        (item) => item.linkedWO?.toString() === woId,
      );
      setWoItems(
        itemsForThisWO.map((item) => ({
          ...item,
          assemblyId: item.assemblyId,
          lineItemId: item.lineItemId || item.key || item._id,
          description: item.costCodeDescription || item.itemDescription || "",
          quantity: item.netRequiredQty || 0,
          remaining: item.netRemaining || 0,
          qtyCharged: item.qtyOrdered || item.qtyCharged || 0,
          qtyPending: item.qtyPending || 0,
          laborHours: item.laborHours || item.netLaborHours || 0,
          laborHoursCharged: item.laborHoursCharged || 0,
          laborHoursRemaining: item.laborHoursRemaining || 0,
          isNewSpec: item.isNewSpec || false,
          closedOutBySizeChange: item.closedOutBySizeChange || false,
          sizeChangedFrom: item.sizeChangedFrom || null,
          hasVariance: item.hasVariance,
          deltaQty: item.deltaQty || 0,
          baseRequiredQty: item.baseRequiredQty || 0,
          originalQuantity: item.baseRequiredQty || item.originalQuantity,
          uom: item.uom || "-",
          bidArea: item.bidArea || "-",
          sectionNumber: item.sectionNumber || "-",
          conditionName: item.conditionName || "-",
          unitRate: item.unitRate || item.price || 0,
          amount: item.amount || 0,
        })),
      );
    } catch (err) {
      console.error("Refetch items failed:", err);
    }
  }, [id, woId]);

  // ── Burden percent from union ──
  useEffect(() => {
    if (!wo?.union || !unions.length) {
      setPieceworkerBurdenPercent(0);
      return;
    }
    if (wo.union === "non-union") {
      setPieceworkerBurdenPercent(0);
      return;
    }
    const matchingUnion = unions.find((u) => u.title === wo.union);
    setPieceworkerBurdenPercent(
      matchingUnion?.pieceworkerBurden != null
        ? Number(matchingUnion.pieceworkerBurden)
        : 0,
    );
  }, [wo?.union, unions]);

  // ── Timesheet summary ──
  useEffect(() => {
    if (wo?.type !== "hourly") {
      setTimesheetSummary(null);
      return;
    }
    const fetchSummary = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get(
          `${API_BASE}/workorders2/${woId}/timesheet-summary`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        setTimesheetSummary(res.data);
      } catch (err) {
        console.error(
          "Timesheet summary fetch failed:",
          err?.response?.status,
          err?.response?.data || err?.message,
        );
        setTimesheetSummary(null);
      }
    };
    fetchSummary();
  }, [wo?.type, woId]);

  // ── Available items for edit mode ──
  useEffect(() => {
    if (!editing || !wo) return;
    const fetchAvailable = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get(
          `${API_BASE}/assemblies/project/${id}/labor?unassignedOnly=true`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        setAvailableItems(res.data);
      } catch (err) {
        console.error("Failed to load available items:", err);
      }
    };
    fetchAvailable();
  }, [editing, wo, id]);

  const existingItemIds = useMemo(() => {
    const ids = new Set(woItems.map((i) => `${i.assemblyId}-${i.lineItemId}`));
    itemsToAdd.forEach((i) => ids.add(`${i.assemblyId}-${i.lineItemId}`));
    return ids;
  }, [woItems, itemsToAdd]);

  const handleAddItems = useCallback((items) => {
    setItemsToAdd((prev) => [...prev, ...items]);
  }, []);

  const itemsToAddWithDetails = useMemo(() => {
    return itemsToAdd.map((item) => {
      const found = availableItems.find(
        (a) => a.assemblyId === item.assemblyId && a._id === item.lineItemId,
      );
      return {
        ...item,
        conditionName: found?.conditionName || "-",
        sectionNumber: found?.sectionNumber || "-",
        description:
          found?.costCodeDescription || found?.itemDescription || "New Item",
        bidArea: found?.bidArea || "-",
        uom: found?.uom || "-",
        quantity: found?.quantity || 0,
        laborHours: found?.laborHours || 0,
        unitRate: found?.unitRate || found?.price || 0,
        amount: found?.amount || 0,
        isNew: true,
      };
    });
  }, [itemsToAdd, availableItems]);

  const displayItems = useMemo(
    () => [
      ...woItems.filter(
        (i) =>
          !itemsToRemove.some(
            (r) =>
              r.assemblyId === i.assemblyId && r.lineItemId === i.lineItemId,
          ),
      ),
      ...itemsToAddWithDetails,
    ],
    [woItems, itemsToRemove, itemsToAddWithDetails],
  );

  const getContractHourlyRate = () => {
    if (wo?.type !== "hourly") return null;
    if (!editedContractLabourItem?.details?.unitCost) return null;
    const uomLower = (editedContractLabourItem.details.uom || "").toLowerCase();
    if (!["hr", "hour", "hrs"].includes(uomLower)) return null;
    return Number(editedContractLabourItem.details.unitCost);
  };
  const contractHourlyRate = getContractHourlyRate();
  const showBackCalculatedHours = !!contractHourlyRate && wo?.type === "hourly";

  const woTableFilterOptions = useMemo(
    () => ({
      conditionName: [
        ...new Set(displayItems.map((i) => i.conditionName).filter(Boolean)),
      ].sort((a, b) =>
        a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" }),
      ),
      sectionNumber: [
        ...new Set(displayItems.map((i) => i.sectionNumber).filter(Boolean)),
      ].sort((a, b) =>
        a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" }),
      ),
      bidArea: [
        ...new Set(displayItems.map((i) => i.bidArea).filter(Boolean)),
      ].sort(),
      uom: [...new Set(displayItems.map((i) => i.uom).filter(Boolean))].sort(),
    }),
    [displayItems],
  );

  const filteredDisplayItems = useMemo(() => {
    let results = displayItems.filter((item) => {
      if (
        woTableFilters.conditionName.length > 0 &&
        !woTableFilters.conditionName.includes(item.conditionName)
      )
        return false;
      if (
        woTableFilters.sectionNumber.length > 0 &&
        !woTableFilters.sectionNumber.includes(item.sectionNumber)
      )
        return false;
      if (
        woTableFilters.bidArea.length > 0 &&
        !woTableFilters.bidArea.includes(item.bidArea)
      )
        return false;
      if (
        woTableFilters.uom.length > 0 &&
        !woTableFilters.uom.includes(item.uom)
      )
        return false;
      return true;
    });

    if (woTableFilters.descriptionSearch) {
      const shaped = results.map((item) => ({
        ...item,
        description:
          item.description ||
          item.costCodeDescription ||
          item.itemDescription ||
          "",
        sectionNumber: item.sectionNumber || "",
        uom: item.uom || "",
        _searchBlob: undefined,
      }));
      results = multiTokenSearch(shaped, woTableFilters.descriptionSearch);
    }

    return results;
  }, [displayItems, woTableFilters]);
  const groupedMainItems = useMemo(() => {
    if (!groupMainTable) return filteredDisplayItems;
    const map = new Map();
    filteredDisplayItems.forEach((item) => {
      const description =
        item.description ||
        item.costCodeDescription ||
        item.itemDescription ||
        "";
      const itemCode =
        item.itemCode || (description || "").split(" ")[0] || "UNKNOWN";
      const key = `${itemCode}-${item.bidArea || "N/A"}-${item.uom || "N/A"}`;
      if (!map.has(key)) {
        map.set(key, {
          ...item,
          itemCode,
          description,
          _count: 0,
          _totalLaborHours: 0,
          _totalAmount: 0,
          _totalQuantity: 0,
          _totalCharged: 0,
          _totalPending: 0,
          _totalRemaining: 0,
          _totalApprovedQty: 0,
          _totalPendingQty: 0,
          _totalApprovedCost: 0,
          _totalPendingCost: 0,
          _ids: [],
        });
      }
      const group = map.get(key);
      group._count += 1;
      group._totalLaborHours += Number(item.laborHours || 0);
      group._totalAmount += Number(item.amount || 0);
      group._totalQuantity += Number(item.quantity || 0);
      group._totalCharged += Number(item.qtyCharged || 0);
      group._totalPending += Number(item.qtyPending || 0);
      group._totalRemaining += Number(item.remaining || 0);
      group._ids.push(`${item.assemblyId}-${item.lineItemId}`);
    });
    return Array.from(map.values());
  }, [filteredDisplayItems, groupMainTable]);

  const selectableRowKeys = useMemo(() => {
    if (wo?.type !== "piecework" || groupMainTable) return new Set();
    return new Set(
      filteredDisplayItems
        .filter((i) => !i.isNew && !i.closedOutBySizeChange)
        .map((i) => `${i.assemblyId}-${i.lineItemId}`),
    );
  }, [wo?.type, groupMainTable, filteredDisplayItems]);

  // Selected rows as full objects (for the modal)
  const selectedRowObjects = useMemo(() => {
    if (selectedRowKeys.size === 0) return [];
    return filteredDisplayItems.filter((i) =>
      selectedRowKeys.has(`${i.assemblyId}-${i.lineItemId}`),
    );
  }, [selectedRowKeys, filteredDisplayItems]);

  // Can we show the batch rate button?
  // All selected items must share the same description (costCodeDescription / itemDescription).
  const batchRateEligible = useMemo(() => {
    if (selectedRowObjects.length < 2) return false;
    // Rate change can be applied to any set of selected piecework rows —
    // description match is helpful to guide the user but not strictly required.
    // We allow any selection of 2+ rows.
    return true;
  }, [selectedRowObjects]);

  // Helper to toggle one row
  const toggleRowSelection = (key) => {
    setSelectedRowKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // Select / deselect all visible selectable rows
  const toggleSelectAll = () => {
    if (
      selectedRowKeys.size === selectableRowKeys.size &&
      selectableRowKeys.size > 0
    ) {
      setSelectedRowKeys(new Set());
    } else {
      setSelectedRowKeys(new Set(selectableRowKeys));
    }
  };

  const allSelected =
    selectableRowKeys.size > 0 &&
    selectedRowKeys.size === selectableRowKeys.size;
  const someSelected =
    selectedRowKeys.size > 0 && selectedRowKeys.size < selectableRowKeys.size;

  // ── 4. CLEAR SELECTION when leaving piecework/edit mode ──────────────────────
  // Add inside the useEffect that watches [wo?.type, woId] (or add a new one):
  useEffect(() => {
    setSelectedRowKeys(new Set());
  }, [wo?.type, groupMainTable]);

  const filteredTotals = useMemo(() => {
    const active = filteredDisplayItems.filter((i) => !i.closedOutBySizeChange);
    return {
      itemCount: active.length,
      estimatedTotal: active.reduce((s, i) => s + (i.amount || 0), 0),
      hoursEstimated: active.reduce((s, i) => s + (i.laborHours || 0), 0),
    };
  }, [filteredDisplayItems]);

  const totals = useMemo(() => {
    const active = woItems.filter((i) => !i.closedOutBySizeChange);
    return {
      itemCount: active.length,
      estimatedTotal: active.reduce((s, i) => s + (i.amount || 0), 0),
      hoursEstimated: active.reduce((s, i) => s + (i.laborHours || 0), 0),
    };
  }, [woItems]);

  // ── Save header ──
  const handleSaveHeader = async () => {
    if (!editing) return;
    if (!editedTitle.trim()) {
      setAlert({
        open: true,
        type: "error",
        title: "Missing Title",
        message: "Work Order title is required.",
      });
      return;
    }
    try {
      setIsLoading(true);
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };
      const payload = { title: editedTitle.trim(), notes: editedNotes.trim() };
      if (
        editedCostCode &&
        editedCostCode !== (wo?.costCode?._id || wo?.costCode || "")
      )
        payload.costCode = editedCostCode;
      if (canChangeType && editedType && editedType !== wo?.type)
        payload.type = editedType;
      const currentType = editedType || wo?.type || "hourly";
      if (
        (currentType === "hourly" || currentType === "piecework") &&
        canChangeType &&
        editedUnion &&
        editedUnion !== (wo?.union || "non-union")
      )
        payload.union = editedUnion;
      const newSelectedId = editedContractLabourItem?.value || null;
      const currentSelectedId =
        wo?.selectedContractLabourItem?._id ||
        wo?.selectedContractLabourItem ||
        null;
      if (newSelectedId !== currentSelectedId)
        payload.selectedContractLabourItem = newSelectedId;
      const res = await axios.put(`${API_BASE}/workorders2/${woId}`, payload, {
        headers,
      });
      setWO(res.data);
      setEditedTitle(res.data.title || "");
      setEditedCostCode(res.data.costCode?._id || res.data.costCode || "");
      setEditedNotes(res.data.notes || "");
      setEditedType(res.data.type || "hourly");
      setEditedUnion(res.data.union || "non-union");
      const refreshedId =
        res.data.selectedContractLabourItem?._id ||
        res.data.selectedContractLabourItem ||
        null;
      setEditedContractLabourItem(
        contractLabourOptions.find((opt) => opt.value === refreshedId) || null,
      );
      setAlert({
        open: true,
        type: "success",
        title: "Saved",
        message: "Work Order details updated successfully.",
      });
    } catch (err) {
      setAlert({
        open: true,
        type: "error",
        title: "Save Failed",
        message:
          err.response?.data?.message || "Could not save work order details.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // ── Save item changes ──
  const handleSaveChanges = async () => {
    if (itemsToAdd.length === 0 && itemsToRemove.length === 0) return;
    try {
      setIsLoading(true);
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };
      if (itemsToAdd.length > 0)
        await axios.post(
          `${API_BASE}/workorders2/${woId}/items/add`,
          { items: itemsToAdd },
          { headers },
        );
      if (itemsToRemove.length > 0)
        await axios.post(
          `${API_BASE}/workorders2/${woId}/items/remove`,
          { items: itemsToRemove },
          { headers },
        );
      setShowAddModal(false);
      setItemsToAdd([]);
      setItemsToRemove([]);
      setEditing(false);
      const woRes = await axios.get(`${API_BASE}/workorders2/${woId}`, {
        headers,
      });
      setWO(woRes.data);
      const netRes = await axios.get(
        `${API_BASE}/net-assemblies/project/${id}`,
        { headers, params: { category: "L" } },
      );
      const allNetLabor = netRes.data.data || [];
      const itemsForThisWO = allNetLabor.filter(
        (item) => item.linkedWO?.toString() === woId,
      );
      setWoItems(
        itemsForThisWO.map((item) => ({
          ...item,
          assemblyId: item.assemblyId,
          lineItemId: item.lineItemId || item.key || item._id,
          description:
            item.costCodeDescription ||
            item.itemDescription ||
            "No description",
          quantity: item.netRequiredQty || item.quantity || 0,
          remaining: item.netRemaining || 0,
          qtyCharged: item.qtyOrdered || item.qtyCharged || 0,
          qtyPending: item.qtyPending || 0,
          laborHours: item.laborHours || item.netLaborHours || 0,
          laborHoursCharged: item.laborHoursCharged || 0,
          laborHoursRemaining: item.laborHoursRemaining || 0,
          isNewSpec: item.isNewSpec || false,
          closedOutBySizeChange: item.closedOutBySizeChange || false,
          hasVariance: item.hasVariance || (item.deltaQty ?? 0) !== 0,
          deltaQty: item.deltaQty || 0,
          baseRequiredQty: item.baseRequiredQty || 0,
          originalQuantity: item.baseRequiredQty || item.originalQuantity,
          uom: item.uom || "-",
          bidArea: item.bidArea || "-",
          sectionNumber: item.sectionNumber || "-",
          conditionName: item.conditionName || "-",
          unitRate: item.unitRate || item.price || 0,
          amount: item.amount || 0,
        })),
      );
      setAlert({
        open: true,
        type: "success",
        title: "Success",
        message: "Work Order updated successfully.",
        onConfirm: () => setAlert((prev) => ({ ...prev, open: false })),
        confirmText: "OK",
      });
    } catch (err) {
      setAlert({
        open: true,
        type: "error",
        title: "Error",
        message:
          err.response?.data?.message ||
          "Failed to save or refresh Work Order.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading)
    return <div className="text-center py-12 text-gray-200">Loading...</div>;
  if (!wo)
    return (
      <div className="text-center py-12 text-gray-200">
        Work Order not found
      </div>
    );

  // ── Helper for per-row progress data ──
  const getRowProgress = (item) => {
    if (!progressStats || wo?.type !== "hourly") return null;
    const key = String(item.lineItemId || item._id || "");
    return progressStats.byItem[key] || null;
  };

  // ============================================================
  // RENDER
  // ============================================================
  return (
    <div className="max-w-full mx-auto px-4 py-6">
      <AlertDialog
        {...alert}
        onClose={() => setAlert({ ...alert, open: false })}
      />

      <AddItemsModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onAdd={handleAddItems}
        availableItems={availableItems}
        existingItemIds={existingItemIds}
      />

      <LaborVarianceModal
        isOpen={varianceItem !== null}
        onClose={() => setVarianceItem(null)}
        onSuccess={async () => {
          setVarianceItem(null);
          // Refresh net-assemblies items (table rows)
          refetchItems();
          // Also refresh the WO header so estimatedTotal reflects the variance
        }}
        item={varianceItem}
        woId={woId}
      />
      <BatchRateChangeModal
        isOpen={batchRateModal.isOpen}
        onClose={() =>
          setBatchRateModal({ isOpen: false, items: [], mode: "selection" })
        }
        onSuccess={(updatedWO) => {
          setBatchRateModal({ isOpen: false, items: [], mode: "selection" });
          setSelectedRowKeys(new Set());

          // Patch the WO header state immediately with recalculated totals
          // so the header card and piecework summary are correct right away.
          if (updatedWO) {
            setWO((prev) =>
              prev
                ? {
                    ...prev,
                    itemCount: updatedWO.itemCount ?? prev.itemCount,
                    estimatedTotal:
                      updatedWO.estimatedTotal ?? prev.estimatedTotal,
                  }
                : prev,
            );
          }

          // Then refetch the net-assemblies items to update the table rows
          refetchItems();
        }}
        woId={woId}
        items={batchRateModal.items}
        mode={batchRateModal.mode}
      />

      {/* ── Header card ── */}
      <div className="mb-6 shadow-xl bg-gray-700 rounded-lg p-6 border border-gray-600">
        <div className="flex justify-between items-start gap-6">
          <div className="flex-1 min-w-0">
            {editing ? (
              <div className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Work Order Title *
                  </label>
                  <input
                    type="text"
                    value={editedTitle}
                    onChange={(e) => setEditedTitle(e.target.value)}
                    placeholder="Enter work order name..."
                    className="w-full text-2xl font-semibold text-gray-100 bg-gray-600 border border-gray-500 rounded-md px-3 py-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Cost Code
                  </label>
                  <Select
                    value={
                      costCodes.find((opt) => opt.value === editedCostCode) ||
                      null
                    }
                    onChange={(opt) => setEditedCostCode(opt ? opt.value : "")}
                    options={costCodes}
                    placeholder="Select cost code..."
                    isClearable
                    isSearchable
                    styles={customStyles}
                    menuPortalTarget={document.body}
                    className="text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1 flex items-center gap-2">
                    Work Order Type
                    {!canChangeType && (
                      <span className="text-xs text-amber-400">
                        (cannot change — charges/timesheets exist)
                      </span>
                    )}
                  </label>
                  <select
                    value={editedType}
                    onChange={(e) => setEditedType(e.target.value)}
                    disabled={!canChangeType}
                    className={`w-full sm:w-64 px-3 py-2 text-sm bg-gray-600 border rounded-md text-gray-200 focus:ring-orange-500 focus:border-orange-500 outline-none ${canChangeType ? "border-gray-500 hover:border-gray-400" : "border-gray-700 opacity-70 cursor-not-allowed"}`}
                  >
                    <option value="hourly">Hourly</option>
                    <option value="piecework">Piecework</option>
                    <option value="subcontractor">Subcontractor</option>
                  </select>
                </div>
                {(editedType === "hourly" || editedType === "piecework") && (
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">
                      Union{" "}
                      {!canChangeType && (
                        <span className="ml-2 text-xs text-amber-400">
                          (locked)
                        </span>
                      )}
                    </label>
                    <Select
                      value={
                        [
                          { value: "non-union", label: "Non-Union" },
                          ...unions.map((u) => ({
                            value: u.title,
                            label: u.title,
                          })),
                        ].find((opt) => opt.value === editedUnion) || null
                      }
                      onChange={(opt) =>
                        setEditedUnion(opt ? opt.value : "non-union")
                      }
                      options={[
                        { value: "non-union", label: "Non-Union" },
                        ...unions.map((u) => ({
                          value: u.title,
                          label: u.title,
                        })),
                      ]}
                      placeholder="Select union..."
                      isClearable={false}
                      isSearchable={false}
                      styles={customStyles}
                      menuPortalTarget={document.body}
                      isDisabled={!canChangeType}
                      className="text-sm"
                    />
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Notes / Description
                  </label>
                  <textarea
                    value={editedNotes}
                    onChange={(e) => setEditedNotes(e.target.value)}
                    rows={3}
                    placeholder="Scope details, special instructions, internal notes..."
                    className="w-full px-3 py-2 text-sm bg-gray-600 border border-gray-500 rounded-md text-gray-200 focus:ring-orange-500 focus:border-orange-500 outline-none resize-y min-h-[80px]"
                  />
                </div>
                {editedType === "hourly" && (
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">
                      Contract Labor Item (Rate Source)
                    </label>
                    <Select
                      value={editedContractLabourItem}
                      onChange={setEditedContractLabourItem}
                      options={contractLabourOptions}
                      placeholder="Select contract labor item (optional)"
                      isClearable
                      isSearchable
                      styles={customStyles}
                      menuPortalTarget={document.body}
                      className="text-sm"
                      noOptionsMessage={() =>
                        "No contract labor items defined for this project"
                      }
                    />
                    {editedContractLabourItem && (
                      <div className="mt-2 text-xs text-gray-400">
                        Selected:{" "}
                        <span className="text-orange-300">
                          {editedContractLabourItem.details?.labourItem
                            ?.title || "No title"}
                        </span>
                        <br />
                        Cost rate:{" "}
                        <span className="text-green-400 font-medium">
                          $
                          {Number(
                            editedContractLabourItem.details?.unitCost || 0,
                          ).toFixed(2)}
                        </span>{" "}
                        / {editedContractLabourItem.details?.uom || "?"}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <>
                <h1 className="text-2xl font-semibold text-white">
                  {wo.title}{" "}
                  <span className="text-gray-400">(WO# {wo.woNumber})</span>
                </h1>
                {wo.notes && (
                  <p className="mt-2 text-sm text-gray-300 whitespace-pre-line">
                    {wo.notes}
                  </p>
                )}
                <p className="mt-3 text-sm text-gray-400">
                  {totals.itemCount} items • Est:{" "}
                  <span className="text-orange-400 font-medium">
                    {formatCurrency(totals.estimatedTotal)}
                  </span>
                  {wo.type === "hourly" && contractHourlyRate > 0 && (
                    <span className="ml-2 text-cyan-400 text-xs">
                      ≈{" "}
                      {(totals.estimatedTotal / contractHourlyRate).toFixed(1)}h
                      budgeted
                    </span>
                  )}
                  <span className="ml-2 text-blue-400 text-xs">
                    (Net quantities)
                  </span>
                </p>
                {wo.type === "piecework" && pieceworkSummary && (
                  <div className="mt-3 flex flex-wrap items-center gap-4 text-sm">
                    <span className="text-gray-400">
                      Charged:{" "}
                      <span className="text-green-400 font-medium">
                        {formatCurrency(pieceworkSummary.chargedAmount)}
                      </span>
                    </span>
                    <span className="text-gray-600">|</span>
                    <span className="text-gray-400">
                      Pending:{" "}
                      <span className="text-yellow-400 font-medium">
                        {formatCurrency(pieceworkSummary.pendingAmount)}
                      </span>
                    </span>
                    <span className="text-gray-600">|</span>
                    <span className="text-gray-400">
                      Remaining:{" "}
                      <span className="text-orange-400 font-medium">
                        {formatCurrency(pieceworkSummary.remainingAmount)}
                      </span>
                    </span>
                  </div>
                )}
              </>
            )}
          </div>

          <div className="flex items-center gap-3 shrink-0">
            {editing ? (
              <>
                <button
                  onClick={() => {
                    setEditedTitle(wo.title || "");
                    setEditedCostCode(wo.costCode?._id || wo.costCode || "");
                    setEditedNotes(wo.notes || "");
                    setEditedType(wo.type || "hourly");
                    setEditing(false);
                  }}
                  className="px-4 py-2 text-sm text-gray-200 bg-gray-600 rounded-md hover:bg-gray-500"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveHeader}
                  disabled={isLoading || !editedTitle.trim()}
                  className={`px-5 py-2 text-sm font-medium rounded-md ${!editedTitle.trim() || isLoading ? "bg-gray-600 text-gray-400 cursor-not-allowed" : "bg-green-600 hover:bg-green-700 text-white"}`}
                >
                  {isLoading ? "Saving..." : "Save Details"}
                </button>
              </>
            ) : (
              <button
                onClick={() => setEditing(true)}
                className="px-4 py-2 text-sm text-white bg-orange-500 rounded-md hover:bg-orange-600"
              >
                Edit WO
              </button>
            )}
            <button
              onClick={() =>
                navigate(`/Projects/${id}`, {
                  state: { initialTab: "Work Orders" },
                })
              }
              className="px-4 py-2 text-sm text-gray-200 bg-gray-600 rounded-md hover:bg-gray-500"
            >
              Back
            </button>
          </div>
        </div>
      </div>

      {/* ── Unified WO Summary card (hourly only) ── */}
      {wo.type === "hourly" &&
        (progressStats || timesheetSummary) &&
        (() => {
          const budgetCost = progressStats?.estimatedCost ?? 0;
          const approvedProgressCost = progressStats?.totalApprovedCost ?? 0;
          const pendingProgressCost = progressStats?.totalPendingCost ?? 0;
          const actualApprovedCost = timesheetSummary?.approved?.totalCost ?? 0;
          const actualPendingCost = timesheetSummary?.pending?.totalCost ?? 0;

          const budgetHours =
            contractHourlyRate > 0 ? budgetCost / contractHourlyRate : null;
          const approvedProgressHours =
            contractHourlyRate > 0
              ? approvedProgressCost / contractHourlyRate
              : null;
          const actualApprovedHours = timesheetSummary?.approved?.hours ?? null;
          const actualPendingHours = timesheetSummary?.pending?.hours ?? null;

          const costVariance = actualApprovedCost - approvedProgressCost;
          const costVarianceIsOver = costVariance > 0;
          const costVariancePct =
            approvedProgressCost > 0
              ? Math.abs(costVariance / approvedProgressCost) * 100
              : null;

          const hoursVariance =
            actualApprovedHours != null && approvedProgressHours != null
              ? actualApprovedHours - approvedProgressHours
              : null;
          const hoursVarianceIsOver =
            hoursVariance != null && hoursVariance > 0;

          const completionPct = progressStats?.completionPct ?? 0;
          const pendingPct = progressStats?.pendingPct ?? 0;

          const budgetUsedPct =
            budgetCost > 0
              ? Math.min(999, (actualApprovedCost / budgetCost) * 100)
              : 0;

          return (
            <div className="mb-6 bg-gray-800 rounded-xl border border-gray-600 overflow-hidden shadow-xl">
              {/* ── Header ── */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-700">
                <div className="flex items-center gap-3 flex-wrap">
                  <h3 className="text-base font-semibold text-white tracking-tight">
                    Work Order Summary
                  </h3>
                  {progressStats && (
                    <span
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${
                        completionPct >= 90
                          ? "bg-green-400/15 text-green-400"
                          : completionPct >= 50
                            ? "bg-blue-400/15 text-blue-400"
                            : "bg-yellow-400/15 text-yellow-400"
                      }`}
                    >
                      <span
                        className={`w-1.5 h-1.5 rounded-full ${
                          completionPct >= 90
                            ? "bg-green-400"
                            : completionPct >= 50
                              ? "bg-blue-400"
                              : "bg-yellow-400"
                        }`}
                      />
                      {completionPct.toFixed(1)}% Installed
                    </span>
                  )}
                  {budgetCost > 0 && actualApprovedCost > 0 && (
                    <span
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${
                        budgetUsedPct > 100
                          ? "bg-red-400/15 text-red-400"
                          : budgetUsedPct >= 80
                            ? "bg-orange-400/15 text-orange-400"
                            : "bg-gray-400/10 text-gray-400"
                      }`}
                    >
                      <span
                        className={`w-1.5 h-1.5 rounded-full ${
                          budgetUsedPct > 100
                            ? "bg-red-400"
                            : budgetUsedPct >= 80
                              ? "bg-orange-400"
                              : "bg-gray-400"
                        }`}
                      />
                      {budgetUsedPct.toFixed(1)}% of Budget Spent
                    </span>
                  )}
                </div>
                <button
                  onClick={() =>
                    navigate(`/Projects/${id}`, {
                      state: { initialTab: "Progress Logs" },
                    })
                  }
                  className="text-xs text-blue-400 hover:text-blue-300 transition-colors shrink-0"
                >
                  View progress logs →
                </button>
              </div>

              {/* ── Installation progress bar ── */}
              {progressStats && progressStats.totalEstimated > 0 && (
                <div className="px-6 pt-4 pb-3 border-b border-gray-700">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-widest">
                      Installation Progress
                    </span>
                    <span className="text-[11px] text-gray-500">
                      {progressStats.totalApproved.toFixed(0)} approved
                      {progressStats.totalPending > 0 &&
                        ` · ${progressStats.totalPending.toFixed(0)} pending`}{" "}
                      / {progressStats.totalEstimated.toFixed(0)} total units
                    </span>
                  </div>
                  <div className="w-full bg-gray-700 rounded-full h-3 overflow-hidden flex">
                    <div
                      className="h-full bg-green-500 transition-all duration-500"
                      style={{ width: `${completionPct}%` }}
                    />
                    <div
                      className="h-full bg-yellow-400/75 transition-all duration-500"
                      style={{ width: `${pendingPct}%` }}
                    />
                  </div>
                  <div className="flex items-center gap-5 mt-1.5">
                    <span className="flex items-center gap-1.5 text-[11px] text-gray-400">
                      <span className="w-2.5 h-2.5 rounded-sm bg-green-500 inline-block" />
                      Approved {completionPct.toFixed(1)}%
                    </span>
                    {pendingPct > 0 && (
                      <span className="flex items-center gap-1.5 text-[11px] text-gray-400">
                        <span className="w-2.5 h-2.5 rounded-sm bg-yellow-400/75 inline-block" />
                        Pending +{pendingPct.toFixed(1)}%
                      </span>
                    )}
                    <span className="flex items-center gap-1.5 text-[11px] text-gray-500">
                      <span className="w-2.5 h-2.5 rounded-sm bg-gray-700 border border-gray-600 inline-block" />
                      Outstanding{" "}
                      {(100 - completionPct - pendingPct).toFixed(1)}%
                    </span>
                  </div>
                </div>
              )}

              {/* ── Three comparison columns ── */}
              <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-gray-700">
                {/* COL 1 — Budget */}
                <div className="px-6 py-5">
                  <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-widest mb-4">
                    Budget
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-400">Total cost</span>
                      <span className="text-sm font-bold text-white">
                        {formatCurrency(budgetCost)}
                      </span>
                    </div>
                    {budgetHours != null && (
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-400">
                          Total hours
                        </span>
                        <span className="text-sm font-bold text-white">
                          {budgetHours.toFixed(1)}h
                        </span>
                      </div>
                    )}
                    {contractHourlyRate > 0 && (
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-400">Rate</span>
                        <span className="text-sm text-gray-300">
                          {formatCurrency(contractHourlyRate)}/hr
                        </span>
                      </div>
                    )}
                    <div className="border-t border-gray-700/60 pt-3">
                      <div className="flex items-center justify-between">
                        <span className="text-[11px] text-gray-500">
                          Progress logs
                        </span>
                        <span className="text-[11px] font-semibold text-blue-400">
                          {progressStats?.logCount ?? 0}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* COL 2 — Progress (cost at budget rate) */}
                <div className="px-6 py-5">
                  <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-widest mb-4">
                    Progress Cost
                    <span className="ml-1.5 normal-case font-normal text-gray-600">
                      at budget rate
                    </span>
                  </p>
                  {progressStats ? (
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-green-500 inline-block" />
                          <span className="text-sm text-gray-300">
                            Approved
                          </span>
                        </div>
                        <div className="text-right">
                          <div className="text-sm font-bold text-green-400">
                            {formatCurrency(approvedProgressCost)}
                          </div>
                          {approvedProgressHours != null && (
                            <div className="text-[10px] text-green-400/70">
                              {approvedProgressHours.toFixed(1)}h equiv.
                            </div>
                          )}
                        </div>
                      </div>
                      {pendingProgressCost > 0 && (
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-yellow-400 inline-block" />
                            <span className="text-sm text-gray-300">
                              Pending
                            </span>
                          </div>
                          <div className="text-right">
                            <div className="text-sm font-semibold text-yellow-400">
                              {formatCurrency(pendingProgressCost)}
                            </div>
                            {contractHourlyRate > 0 && (
                              <div className="text-[10px] text-yellow-400/70">
                                {(
                                  pendingProgressCost / contractHourlyRate
                                ).toFixed(1)}
                                h equiv.
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-gray-500 inline-block" />
                          <span className="text-sm text-gray-400">
                            Outstanding
                          </span>
                        </div>
                        <div className="text-right">
                          <div className="text-sm font-medium text-orange-300">
                            {formatCurrency(progressStats.totalRemainingCost)}
                          </div>
                          {contractHourlyRate > 0 &&
                            progressStats.totalRemainingCost > 0 && (
                              <div className="text-[10px] text-orange-300/70">
                                {(
                                  progressStats.totalRemainingCost /
                                  contractHourlyRate
                                ).toFixed(1)}
                                h equiv.
                              </div>
                            )}
                        </div>
                      </div>
                      <div className="border-t border-gray-700/60 pt-2 flex items-center justify-between">
                        <span className="text-[11px] text-gray-500">
                          Approved + Pending
                        </span>
                        <span className="text-[11px] font-semibold text-gray-300">
                          {formatCurrency(
                            approvedProgressCost + pendingProgressCost,
                          )}
                        </span>
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 italic">
                      No progress logs yet.
                    </p>
                  )}
                </div>

                {/* COL 3 — Actual (timesheets) */}
                <div className="px-6 py-5">
                  <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-widest mb-4">
                    Actual Cost
                    <span className="ml-1.5 normal-case font-normal text-gray-600">
                      via timesheets
                    </span>
                  </p>
                  {timesheetSummary ? (
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-green-500 inline-block" />
                          <span className="text-sm text-gray-300">
                            Approved
                          </span>
                        </div>
                        <div className="text-right">
                          <div className="text-sm font-bold text-orange-400">
                            {formatCurrency(actualApprovedCost)}
                          </div>
                          {actualApprovedHours != null && (
                            <div className="text-[10px] text-orange-400/70">
                              {actualApprovedHours}h ·{" "}
                              {timesheetSummary.approved?.count ?? 0} sheets
                            </div>
                          )}
                        </div>
                      </div>
                      {(timesheetSummary.approved?.burdenCost || 0) > 0 && (
                        <div className="flex flex-col gap-1 pl-4 text-[11px] text-gray-500">
                          <div className="flex justify-between">
                            <span>Base pay</span>
                            <span>
                              {formatCurrency(
                                timesheetSummary.approved.subtotal,
                              )}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span>Burden</span>
                            <span>
                              {formatCurrency(
                                timesheetSummary.approved.burdenCost,
                              )}
                            </span>
                          </div>
                          {(timesheetSummary.approved?.wsibCost || 0) > 0 && (
                            <div className="flex justify-between">
                              <span>WSIB</span>
                              <span>
                                {formatCurrency(
                                  timesheetSummary.approved.wsibCost,
                                )}
                              </span>
                            </div>
                          )}
                        </div>
                      )}
                      {(timesheetSummary.pending?.count || 0) > 0 && (
                        <>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <span className="w-2 h-2 rounded-full bg-yellow-400 inline-block" />
                              <span className="text-sm text-gray-300">
                                Pending
                              </span>
                            </div>
                            <div className="text-right">
                              <div className="text-sm font-semibold text-yellow-400">
                                {formatCurrency(actualPendingCost)}
                              </div>
                              {actualPendingHours != null && (
                                <div className="text-[10px] text-yellow-400/70">
                                  {actualPendingHours}h ·{" "}
                                  {timesheetSummary.pending.count} sheets
                                </div>
                              )}
                            </div>
                          </div>
                        </>
                      )}

                      {/* ── Variance callout vs progress cost ── */}
                      {approvedProgressCost > 0 && actualApprovedCost > 0 && (
                        <div
                          className={`mt-1 rounded-lg px-3 py-2.5 border ${
                            costVarianceIsOver
                              ? "bg-red-400/8 border-red-500/30"
                              : "bg-green-400/8 border-green-500/30"
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <span className="text-[11px] text-gray-400">
                              Actual vs progress cost
                            </span>
                            <span
                              className={`text-sm font-bold ${costVarianceIsOver ? "text-red-400" : "text-green-400"}`}
                            >
                              {costVarianceIsOver ? "+" : "−"}
                              {formatCurrency(Math.abs(costVariance))}
                            </span>
                          </div>
                          {costVariancePct != null && (
                            <div className="flex items-center justify-between mt-0.5">
                              <span className="text-[10px] text-gray-500">
                                {costVarianceIsOver
                                  ? "Over budget rate"
                                  : "Under budget rate"}
                              </span>
                              <span
                                className={`text-[10px] font-semibold ${costVarianceIsOver ? "text-red-400/80" : "text-green-400/80"}`}
                              >
                                {costVariancePct.toFixed(1)}%
                              </span>
                            </div>
                          )}
                          {/* Hours variance line */}
                          {hoursVariance != null && budgetHours != null && (
                            <div className="flex items-center justify-between mt-1 pt-1 border-t border-gray-700/40">
                              <span className="text-[10px] text-gray-500">
                                Actual vs est. hours
                              </span>
                              <span
                                className={`text-[10px] font-semibold ${hoursVarianceIsOver ? "text-red-400" : "text-green-400"}`}
                              >
                                {hoursVarianceIsOver ? "+" : "−"}
                                {Math.abs(hoursVariance).toFixed(1)}h
                                <span className="ml-1 text-gray-500 font-normal">
                                  ({actualApprovedHours}h actual /{" "}
                                  {approvedProgressHours?.toFixed(1)}h progress)
                                </span>
                              </span>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Hours vs budget summary */}
                      {budgetHours != null && actualApprovedHours != null && (
                        <div className="border-t border-gray-700/60 pt-2 space-y-1.5">
                          <div className="flex items-center justify-between">
                            <span className="text-[11px] text-gray-500">
                              Budget hours
                            </span>
                            <span className="text-[11px] font-semibold text-gray-300">
                              {budgetHours.toFixed(1)}h
                            </span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-[11px] text-gray-500">
                              Actual hours charged
                            </span>
                            <span
                              className={`text-[11px] font-semibold ${
                                actualApprovedHours > budgetHours
                                  ? "text-red-400"
                                  : "text-green-400"
                              }`}
                            >
                              {actualApprovedHours}h
                              {actualPendingHours != null &&
                                actualPendingHours > 0 && (
                                  <span className="text-yellow-400 ml-1">
                                    (+{actualPendingHours}h pending)
                                  </span>
                                )}
                            </span>
                          </div>
                          <div className="w-full bg-gray-700 rounded-full h-1.5 overflow-hidden flex mt-1">
                            <div
                              className={`h-full rounded-full transition-all ${
                                actualApprovedHours / budgetHours > 1
                                  ? "bg-red-500"
                                  : "bg-orange-400"
                              }`}
                              style={{
                                width: `${Math.min(100, (actualApprovedHours / budgetHours) * 100)}%`,
                              }}
                            />
                          </div>
                          <div className="text-right text-[10px] text-gray-500">
                            {(
                              (actualApprovedHours / budgetHours) *
                              100
                            ).toFixed(1)}
                            % of budget hours used
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 italic">
                      No timesheets recorded yet.
                    </p>
                  )}
                </div>
              </div>
            </div>
          );
        })()}

      {/* ── Piecework Summary ── */}
      {wo.type === "piecework" && pieceworkSummary && (
        <div className="mb-6 p-6 bg-gray-800 rounded-lg border border-gray-600">
          <h3 className="text-lg font-semibold text-white mb-4">
            Piecework Summary
            {pieceworkerBurdenPercent > 0 && (
              <span className="ml-3 text-sm font-normal text-gray-400">
                Burden: {pieceworkerBurdenPercent}% • Union: {wo.union}
              </span>
            )}
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
            <div className="text-center">
              <div className="text-2xl font-bold text-orange-400">
                {formatCurrency(pieceworkSummary.estimatedTotal)}
              </div>
              <div className="text-sm text-gray-400 mt-1">Estimated Total</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-400">
                {formatCurrency(pieceworkSummary.chargedAmount)}
              </div>
              <div className="text-sm text-gray-400 mt-1">Charged Amount</div>
              <div className="text-xs text-gray-500 mt-0.5">
                {pieceworkSummary.totalQtyCharged.toFixed(2)} qty
              </div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-yellow-400">
                {formatCurrency(pieceworkSummary.pendingAmount)}
              </div>
              <div className="text-sm text-gray-400 mt-1">Pending Amount</div>
              <div className="text-xs text-gray-500 mt-0.5">
                {pieceworkSummary.totalQtyPending.toFixed(2)} qty
              </div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-orange-300">
                {formatCurrency(pieceworkSummary.remainingAmount)}
              </div>
              <div className="text-sm text-gray-400 mt-1">Remaining Amount</div>
              <div className="text-xs text-gray-500 mt-0.5">
                {pieceworkSummary.totalQtyRemaining.toFixed(2)} qty
              </div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-300">
                {pieceworkSummary.logCount}
              </div>
              <div className="text-sm text-gray-400 mt-1">Piecework Logs</div>
            </div>
          </div>
          {pieceworkSummary.estimatedTotal > 0 && (
            <div className="mt-4 pt-3 border-t border-gray-700">
              <div className="flex items-center gap-3">
                <div className="flex-1 bg-gray-700 rounded-full h-2.5 overflow-hidden">
                  <div
                    className="h-full rounded-full bg-gradient-to-r from-green-500 to-green-400"
                    style={{
                      width: `${Math.min(100, (pieceworkSummary.chargedAmount / pieceworkSummary.estimatedTotal) * 100)}%`,
                    }}
                  />
                </div>
                <span className="text-sm text-gray-400 whitespace-nowrap">
                  {(
                    (pieceworkSummary.chargedAmount /
                      pieceworkSummary.estimatedTotal) *
                    100
                  ).toFixed(1)}
                  % charged
                </span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Items Table ── */}
      <div className="bg-gray-700 p-6 rounded-lg border border-gray-600 shadow-xl">
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center gap-6">
            <h2 className="text-lg font-semibold text-white">
              Work Order Items
              <span className="ml-2 text-sm font-normal text-gray-400">
                (Net quantities incl. variance/change order)
              </span>
            </h2>
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-300 whitespace-nowrap">
                Group by Item/Area/UOM
              </label>
              <div
                className={`relative inline-flex h-6 w-11 cursor-pointer items-center rounded-full transition-colors ${groupMainTable ? "bg-orange-600" : "bg-gray-600"}`}
                onClick={() => setGroupMainTable(!groupMainTable)}
              >
                <span
                  className={`absolute left-1 top-1 block h-4 w-4 rounded-full bg-white transition-transform ${groupMainTable ? "translate-x-5" : "translate-x-0"}`}
                />
              </div>
            </div>
          </div>

          <div className="flex space-x-4">
            {!editing && (
              <button
                onClick={() => setEditing(true)}
                className="px-4 py-2 text-white bg-orange-400 rounded-md hover:bg-orange-500"
              >
                Edit WO
              </button>
            )}
            {editing && (
              <>
                <button
                  onClick={() => setShowAddModal(true)}
                  className="px-4 py-2 text-white bg-green-600 rounded-md hover:bg-green-700 flex items-center gap-2"
                >
                  <span className="text-lg">+</span> Add Labor Items
                </button>
                <button
                  onClick={() => setEditing(false)}
                  className="px-4 py-2 text-gray-200 bg-gray-600 rounded-md hover:bg-gray-500"
                >
                  Cancel
                </button>
              </>
            )}
          </div>
        </div>

        {/* Filter bar */}
        <div className="bg-gray-800 rounded-lg p-3 mb-3">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-4 text-xl">
              <span className="text-gray-400">
                <span className="text-white font-semibold">
                  {groupMainTable
                    ? groupedMainItems.length.toLocaleString()
                    : filteredDisplayItems.length.toLocaleString()}
                </span>
                {filteredDisplayItems.length !== displayItems.length && (
                  <span className="text-gray-500">
                    {" "}
                    / {displayItems.length.toLocaleString()}
                  </span>
                )}{" "}
                items
              </span>
              <span className="text-gray-600">|</span>
              <span className="text-orange-400 font-medium">
                {formatCurrency(filteredTotals.estimatedTotal)}
              </span>
            </div>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative">
              <input
                type="text"
                value={woTableFilters.descriptionSearch}
                onChange={(e) =>
                  setWoTableFilters((prev) => ({
                    ...prev,
                    descriptionSearch: e.target.value,
                  }))
                }
                placeholder="Search..."
                className="w-40 px-2 py-1 text-xs bg-gray-700 border border-gray-600 rounded text-gray-200 placeholder-gray-500 focus:border-orange-500 focus:outline-none"
              />
              {woTableFilters.descriptionSearch && (
                <button
                  onClick={() =>
                    setWoTableFilters((prev) => ({
                      ...prev,
                      descriptionSearch: "",
                    }))
                  }
                  className="absolute right-1 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                >
                  <svg
                    className="w-3 h-3"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              )}
            </div>
            <span className="text-gray-600">|</span>
            {woTableFilters.conditionName.length > 0 && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[11px] font-medium bg-teal-600/80 text-white rounded">
                Condition:{" "}
                {woTableFilters.conditionName.length === 1
                  ? woTableFilters.conditionName[0]
                  : `${woTableFilters.conditionName.length} selected`}
                <button
                  onClick={() =>
                    setWoTableFilters((prev) => ({
                      ...prev,
                      conditionName: [],
                    }))
                  }
                  className="hover:text-teal-200"
                >
                  ×
                </button>
              </span>
            )}
            {woTableFilters.sectionNumber.length > 0 && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[11px] font-medium bg-purple-600/80 text-white rounded">
                Section:{" "}
                {woTableFilters.sectionNumber.length === 1
                  ? woTableFilters.sectionNumber[0]
                  : `${woTableFilters.sectionNumber.length} selected`}
                <button
                  onClick={() =>
                    setWoTableFilters((prev) => ({
                      ...prev,
                      sectionNumber: [],
                    }))
                  }
                  className="hover:text-purple-200"
                >
                  ×
                </button>
              </span>
            )}
            {woTableFilters.bidArea.length > 0 && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[11px] font-medium bg-green-600/80 text-white rounded">
                Bid Area:{" "}
                {woTableFilters.bidArea.length === 1
                  ? woTableFilters.bidArea[0]
                  : `${woTableFilters.bidArea.length} selected`}
                <button
                  onClick={() =>
                    setWoTableFilters((prev) => ({ ...prev, bidArea: [] }))
                  }
                  className="hover:text-green-200"
                >
                  ×
                </button>
              </span>
            )}
            {woTableFilters.uom.length > 0 && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[11px] font-medium bg-indigo-600/80 text-white rounded">
                UOM:{" "}
                {woTableFilters.uom.length === 1
                  ? woTableFilters.uom[0]
                  : `${woTableFilters.uom.length} selected`}
                <button
                  onClick={() =>
                    setWoTableFilters((prev) => ({ ...prev, uom: [] }))
                  }
                  className="hover:text-indigo-200"
                >
                  ×
                </button>
              </span>
            )}
            {Object.values(woTableFilters).some((v) =>
              Array.isArray(v) ? v.length > 0 : !!v,
            ) && (
              <button
                onClick={() =>
                  setWoTableFilters({
                    conditionName: [],
                    sectionNumber: [],
                    bidArea: [],
                    uom: [],
                    descriptionSearch: "",
                  })
                }
                className="px-1.5 py-0.5 text-[11px] font-medium text-red-400 hover:text-red-300 hover:bg-red-900/30 rounded"
              >
                Clear all
              </button>
            )}
          </div>
        </div>

        {wo.type === "piecework" &&
          !groupMainTable &&
          selectedRowKeys.size > 0 && (
            <div className="mb-3 px-4 py-3 bg-purple-900/30 border border-purple-700/50 rounded-lg flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <span className="text-sm font-semibold text-purple-200">
                  {selectedRowKeys.size} item
                  {selectedRowKeys.size !== 1 ? "s" : ""} selected
                </span>
                <button
                  type="button"
                  onClick={() => setSelectedRowKeys(new Set())}
                  className="text-xs text-purple-400 hover:text-purple-200"
                >
                  Clear
                </button>
              </div>
              <div className="flex items-center gap-2">
                {!batchRateEligible && selectedRowKeys.size === 1 && (
                  <span className="text-xs text-gray-500">
                    Select 2+ items to batch update rate
                  </span>
                )}
                <button
                  type="button"
                  disabled={selectedRowKeys.size < 1}
                  onClick={() =>
                    setBatchRateModal({
                      isOpen: true,
                      items: selectedRowObjects,
                      mode: "selection",
                    })
                  }
                  className={`px-4 py-1.5 text-sm font-semibold rounded-lg transition-colors ${
                    selectedRowKeys.size < 1
                      ? "bg-gray-700 text-gray-500 cursor-not-allowed"
                      : "bg-purple-600 hover:bg-purple-700 text-white"
                  }`}
                >
                  Change Rate for {selectedRowKeys.size} Item
                  {selectedRowKeys.size !== 1 ? "s" : ""}
                </button>
              </div>
            </div>
          )}

        {/* Table */}
        <div
          className="rounded-lg overflow-x-auto shadow-xl"
          style={{ minHeight: "400px" }}
        >
          <table className="min-w-full divide-y divide-gray-600 bg-gray-700">
            <thead className="bg-gray-800">
              <tr>
                {wo.type === "piecework" && !groupMainTable && (
                  <th className="w-8 px-2 py-2 text-center">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      ref={(el) => {
                        if (el) el.indeterminate = someSelected;
                      }}
                      onChange={toggleSelectAll}
                      className="h-4 w-4 text-purple-500 rounded border-gray-500 cursor-pointer"
                      title="Select all"
                    />
                  </th>
                )}
                {/* Condition — filterable, hidden in group mode */}
                {!groupMainTable && (
                  <th
                    className={`px-3 py-2 text-left text-xs font-medium cursor-pointer relative hover:text-orange-300 ${woTableFilters.conditionName.length > 0 ? "text-orange-400" : "text-gray-400"} uppercase`}
                    onClick={() =>
                      setShowWoTableFilter((prev) =>
                        prev === "conditionName" ? null : "conditionName",
                      )
                    }
                  >
                    <span className="flex items-center gap-0.5">
                      Condition
                      <svg
                        className="w-3 h-3"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                      {woTableFilters.conditionName.length > 0 && (
                        <span className="ml-0.5 px-1 py-0.5 text-[9px] bg-orange-500 text-white rounded-full">
                          {woTableFilters.conditionName.length}
                        </span>
                      )}
                    </span>
                    {showWoTableFilter === "conditionName" && (
                      <div
                        ref={woTableConditionRef}
                        className="absolute left-0 top-full z-[99999] mt-1 bg-gray-700 border border-gray-600 rounded-lg shadow-2xl"
                        style={{ minWidth: "280px" }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="p-3">
                          <Select
                            isMulti
                            closeMenuOnSelect={false}
                            hideSelectedOptions={false}
                            options={(
                              woTableFilterOptions.conditionName || []
                            ).map((v) => ({ value: v, label: v }))}
                            value={(woTableFilterOptions.conditionName || [])
                              .map((v) => ({ value: v, label: v }))
                              .filter((opt) =>
                                woTableFilters.conditionName.includes(
                                  opt.value,
                                ),
                              )}
                            onChange={(opts) =>
                              setWoTableFilters((prev) => ({
                                ...prev,
                                conditionName: opts
                                  ? opts.map((o) => o.value)
                                  : [],
                              }))
                            }
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            styles={{
                              ...multiSelectFilterStyles,
                              menuPortal: (base) => ({
                                ...base,
                                zIndex: 999999,
                              }),
                            }}
                          />
                        </div>
                      </div>
                    )}
                  </th>
                )}

                {/* Section — filterable */}
                <th
                  className={`px-3 py-2 text-left text-xs font-medium cursor-pointer relative hover:text-orange-300 ${woTableFilters.sectionNumber.length > 0 ? "text-orange-400" : "text-gray-400"} uppercase`}
                  onClick={() =>
                    setShowWoTableFilter((prev) =>
                      prev === "sectionNumber" ? null : "sectionNumber",
                    )
                  }
                >
                  <span className="flex items-center gap-0.5">
                    Section
                    <svg
                      className="w-3 h-3"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                    {woTableFilters.sectionNumber.length > 0 && (
                      <span className="ml-0.5 px-1 py-0.5 text-[9px] bg-orange-500 text-white rounded-full">
                        {woTableFilters.sectionNumber.length}
                      </span>
                    )}
                  </span>
                  {showWoTableFilter === "sectionNumber" && (
                    <div
                      ref={woTableSectionRef}
                      className="absolute left-0 top-full z-[99999] mt-1 bg-gray-700 border border-gray-600 rounded-lg shadow-2xl"
                      style={{ minWidth: "180px" }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="p-3">
                        <Select
                          isMulti
                          closeMenuOnSelect={false}
                          hideSelectedOptions={false}
                          options={(
                            woTableFilterOptions.sectionNumber || []
                          ).map((v) => ({ value: v, label: v }))}
                          value={(woTableFilterOptions.sectionNumber || [])
                            .map((v) => ({ value: v, label: v }))
                            .filter((opt) =>
                              woTableFilters.sectionNumber.includes(opt.value),
                            )}
                          onChange={(opts) =>
                            setWoTableFilters((prev) => ({
                              ...prev,
                              sectionNumber: opts
                                ? opts.map((o) => o.value)
                                : [],
                            }))
                          }
                          menuPortalTarget={document.body}
                          menuPosition="fixed"
                          styles={{
                            ...multiSelectFilterStyles,
                            menuPortal: (base) => ({ ...base, zIndex: 999999 }),
                          }}
                        />
                      </div>
                    </div>
                  )}
                </th>

                <th className="px-3 py-2 text-left text-xs font-medium text-gray-400 uppercase">
                  Description
                </th>

                {/* Bid Area — filterable */}
                <th
                  className={`px-3 py-2 text-left text-xs font-medium cursor-pointer relative hover:text-orange-300 ${woTableFilters.bidArea.length > 0 ? "text-orange-400" : "text-gray-400"} uppercase`}
                  onClick={() =>
                    setShowWoTableFilter((prev) =>
                      prev === "bidArea" ? null : "bidArea",
                    )
                  }
                >
                  <span className="flex items-center gap-0.5">
                    Bid Area
                    <svg
                      className="w-3 h-3"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                    {woTableFilters.bidArea.length > 0 && (
                      <span className="ml-0.5 px-1 py-0.5 text-[9px] bg-orange-500 text-white rounded-full">
                        {woTableFilters.bidArea.length}
                      </span>
                    )}
                  </span>
                  {showWoTableFilter === "bidArea" && (
                    <div
                      ref={woTableBidAreaRef}
                      className="absolute left-0 top-full z-[99999] mt-1 bg-gray-700 border border-gray-600 rounded-lg shadow-2xl"
                      style={{ minWidth: "280px" }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="p-3">
                        <Select
                          isMulti
                          closeMenuOnSelect={false}
                          hideSelectedOptions={false}
                          options={(woTableFilterOptions.bidArea || []).map(
                            (v) => ({ value: v, label: v }),
                          )}
                          value={(woTableFilterOptions.bidArea || [])
                            .map((v) => ({ value: v, label: v }))
                            .filter((opt) =>
                              woTableFilters.bidArea.includes(opt.value),
                            )}
                          onChange={(opts) =>
                            setWoTableFilters((prev) => ({
                              ...prev,
                              bidArea: opts ? opts.map((o) => o.value) : [],
                            }))
                          }
                          menuPortalTarget={document.body}
                          menuPosition="fixed"
                          styles={{
                            ...multiSelectFilterStyles,
                            menuPortal: (base) => ({ ...base, zIndex: 999999 }),
                          }}
                        />
                      </div>
                    </div>
                  )}
                </th>

                <th className="px-3 py-2 text-right text-xs font-medium text-gray-400 uppercase">
                  Net Req.
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-400 uppercase">
                  UOM
                </th>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-400 uppercase">
                  Original
                </th>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-400 uppercase">
                  Variance Δ
                </th>

                {/* Progress columns — hourly only, replace charged/pending/remaining */}
                {wo.type === "hourly" ? (
                  <>
                    <th className="px-3 py-2 text-right text-xs font-medium text-green-400 uppercase whitespace-nowrap">
                      <div>Approved</div>
                      <div className="text-[10px] text-green-400/60 font-normal">
                        Progress Cost
                      </div>
                    </th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-yellow-400 uppercase whitespace-nowrap">
                      <div>Pending</div>
                      <div className="text-[10px] text-yellow-400/60 font-normal">
                        Progress Cost
                      </div>
                    </th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-orange-300 uppercase whitespace-nowrap">
                      <div>Outstanding</div>
                      <div className="text-[10px] text-orange-300/60 font-normal">
                        Progress Cost
                      </div>
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-blue-400 uppercase whitespace-nowrap">
                      Progress
                    </th>
                  </>
                ) : (
                  <>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-400 uppercase">
                      Charged
                    </th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-400 uppercase">
                      Pending
                    </th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-400 uppercase">
                      Remaining
                    </th>
                  </>
                )}

                {showBackCalculatedHours && (
                  <th className="px-3 py-2 text-right text-xs font-medium text-cyan-400 uppercase">
                    Est. Hours
                    <div className="text-[10px] text-cyan-300/80">
                      ${contractHourlyRate.toFixed(2)}/hr
                    </div>
                  </th>
                )}
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-400 uppercase">
                  Amount
                </th>
                <th
                  className="px-3 py-2 text-right text-xs font-medium text-gray-400 uppercase whitespace-nowrap"
                  title={`Actual Rate / Foreman Rate`}
                >
                  Rate
                  <div className="text-[10px] text-gray-500">
                    Actual / Foreman
                  </div>
                </th>
                {wo.type === "piecework" && (
                  <th className="px-3 py-2 text-center text-xs font-medium text-gray-400 uppercase">
                    Actions
                  </th>
                )}
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-600 text-white">
              {(groupMainTable ? groupedMainItems : filteredDisplayItems).map(
                (item, idx) => {
                  const isGroup = groupMainTable && (item._count ?? 0) > 1;
                  const isHistorical = item.isHistoricalPriceLine === true;
                  const hasPriceChange =
                    item.isPriceChangeRow === true ||
                    item.hasPriceChange === true;
                  const hasVariance =
                    item.hasVariance || (item.deltaQty ?? 0) !== 0;
                  const isClosed = item.closedOutBySizeChange === true;

                  const netQty = isGroup
                    ? (item._totalQuantity ?? 0)
                    : (item.netRequiredQty ?? item.quantity ?? 0);
                  const origQty = isGroup ? null : (item.baseRequiredQty ?? 0);
                  const delta = isGroup
                    ? null
                    : (item.deltaQty ?? item.coTotalDeltaQty ?? 0);

                  // Progress data for hourly rows
                  const rowKey = String(item.lineItemId || item._id || "");
                  const rowProgress =
                    !isGroup && wo.type === "hourly" && progressStats
                      ? progressStats.byItem[rowKey] || {
                          estimated: netQty,
                          approved: 0,
                          pending: 0,
                          rate: item.unitRate || 0,
                        }
                      : null;

                  const rowApproved = rowProgress?.approved ?? 0;
                  const rowPending = rowProgress?.pending ?? 0;
                  const rowRate = rowProgress?.rate ?? item.unitRate ?? 0;
                  const rowApprovedCost = rowApproved * rowRate;
                  const rowOutstanding = Math.max(
                    0,
                    netQty - rowApproved - rowPending,
                  );
                  const approvedPct =
                    netQty > 0
                      ? Math.min(100, (rowApproved / netQty) * 100)
                      : 0;
                  const pendingPct =
                    netQty > 0
                      ? Math.min(100 - approvedPct, (rowPending / netQty) * 100)
                      : 0;
                  const progressColor =
                    approvedPct >= 90
                      ? "text-green-400"
                      : approvedPct >= 50
                        ? "text-blue-400"
                        : "text-yellow-400";
                  const progressBarColor =
                    approvedPct >= 90
                      ? "bg-green-500"
                      : approvedPct >= 50
                        ? "bg-blue-500"
                        : "bg-yellow-400";

                  // Piecework charged/pending/remaining
                  const charged = isGroup
                    ? (item._totalCharged ?? 0)
                    : (item.qtyOrdered ?? item.qtyCharged ?? 0);
                  const pendingQty = isGroup
                    ? (item._totalPending ?? 0)
                    : (item.qtyPending ?? 0);
                  const remaining = isGroup
                    ? (item._totalRemaining ?? 0)
                    : (item.netRemaining ?? 0);

                  return (
                    <tr
                      key={
                        isGroup
                          ? item.key
                          : `${item.assemblyId}-${item.lineItemId}`
                      }
                      className={`hover:bg-gray-600 transition-colors ${item.isNew ? "bg-green-900/20" : ""} ${isClosed ? "bg-yellow-900/20 opacity-75" : ""} ${isHistorical ? "bg-gray-800/70" : ""}`}
                    >
                      {wo.type === "piecework" && !groupMainTable && (
                        <td
                          className="px-2 py-2 text-center"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {!item.isNew && !item.closedOutBySizeChange ? (
                            <input
                              type="checkbox"
                              checked={selectedRowKeys.has(
                                `${item.assemblyId}-${item.lineItemId}`,
                              )}
                              onChange={() =>
                                toggleRowSelection(
                                  `${item.assemblyId}-${item.lineItemId}`,
                                )
                              }
                              className="h-4 w-4 text-purple-500 rounded border-gray-500 cursor-pointer"
                            />
                          ) : (
                            <span className="text-gray-700">—</span>
                          )}
                        </td>
                      )}

                      {/* Condition */}
                      {!groupMainTable && (
                        <td className="px-3 py-2 text-sm">
                          <div className="flex flex-col gap-1">
                            <span className="font-medium">
                              {item.conditionName || "—"}
                            </span>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {item.isNewSpec && (
                                <span className="px-1.5 py-0.5 text-[10px] bg-blue-700/50 text-blue-200 rounded-full">
                                  SIZE CHANGE
                                </span>
                              )}
                              {isClosed && (
                                <span className="px-1.5 py-0.5 text-[10px] bg-yellow-600/60 text-yellow-200 rounded-full">
                                  CLOSED
                                </span>
                              )}
                              {hasVariance && !item.isNewSpec && !isClosed && (
                                <span className="px-1.5 py-0.5 text-[10px] bg-purple-700/50 text-purple-200 rounded-full">
                                  VARIANCE
                                </span>
                              )}
                              {isHistorical && (
                                <span className="px-1.5 py-0.5 text-[10px] bg-gray-600 text-gray-300 rounded-full">
                                  OLD PRICE
                                </span>
                              )}
                              {hasPriceChange && !isHistorical && (
                                <span className="px-1.5 py-0.5 text-[10px] bg-indigo-700/50 text-indigo-200 rounded-full">
                                  NEW PRICE
                                </span>
                              )}
                            </div>
                          </div>
                        </td>
                      )}

                      <td className="px-3 py-2 text-sm">
                        {item.sectionNumber || "—"}
                      </td>

                      <td
                        className="px-3 py-3 text-sm max-w-[280px] line-clamp-2 break-words whitespace-normal"
                        title={item.description}
                      >
                        {isGroup ? (
                          <div className="flex flex-col gap-0.5">
                            <span className="font-medium">
                              {item.description || "—"}
                            </span>
                            <span className="text-xs text-gray-400">
                              ({item._count} items grouped)
                            </span>
                          </div>
                        ) : (
                          item.description || "No Description"
                        )}
                      </td>

                      <td className="px-3 py-2 text-sm">
                        {item.bidArea || "—"}
                      </td>

                      {/* Net Required */}
                      <td className="px-3 py-2 text-sm text-right font-medium">
                        {isClosed ? (
                          <span className="text-gray-500 line-through">—</span>
                        ) : (
                          formatQty(netQty)
                        )}
                      </td>

                      <td className="px-3 py-2 text-sm font-medium text-gray-300">
                        {item.uom || "—"}
                      </td>

                      {/* Original */}
                      <td className="px-3 py-2 text-sm text-right text-gray-400 text-xs">
                        {origQty > 0 && Math.abs(origQty - netQty) > 0.001
                          ? formatQty(origQty)
                          : "—"}
                      </td>

                      {/* Delta */}
                      {/* Delta */}
                      <td className="px-3 py-2 text-sm text-right font-medium">
                        {delta !== null && Math.abs(delta) > 0.001 ? (
                          <span
                            className={
                              delta > 0 ? "text-purple-400" : "text-red-400"
                            }
                          >
                            {delta > 0 ? "+" : ""}
                            {formatQty(delta)}
                          </span>
                        ) : (
                          "—"
                        )}
                      </td>

                      {/* ── Progress columns (hourly) OR Charged/Pending/Remaining (piecework/other) ── */}
                      {wo.type === "hourly" ? (
                        <>
                          {/* Approved cost */}
                          <td className="px-3 py-2 text-sm text-right text-green-400 font-medium">
                            {isGroup ? (
                              "—"
                            ) : rowApprovedCost > 0 ? (
                              formatCurrency(rowApprovedCost)
                            ) : (
                              <span className="text-gray-600">—</span>
                            )}
                          </td>

                          {/* Pending cost */}
                          <td className="px-3 py-2 text-sm text-right text-yellow-400">
                            {isGroup
                              ? "—"
                              : (() => {
                                  const pendingCost = rowPending * rowRate;
                                  return pendingCost > 0 ? (
                                    formatCurrency(pendingCost)
                                  ) : (
                                    <span className="text-gray-600">—</span>
                                  );
                                })()}
                          </td>

                          {/* Outstanding cost */}
                          <td className="px-3 py-2 text-sm text-right">
                            {isGroup ? (
                              "—"
                            ) : isClosed ? (
                              <span className="text-gray-500">—</span>
                            ) : (
                              (() => {
                                const outstandingCost =
                                  rowOutstanding * rowRate;
                                return outstandingCost > 0 ? (
                                  <span className="text-orange-300 font-medium">
                                    {formatCurrency(outstandingCost)}
                                  </span>
                                ) : (
                                  <span className="text-green-400 font-bold">
                                    ✓
                                  </span>
                                );
                              })()
                            )}
                          </td>

                          {/* Progress bar + % */}
                          <td className="px-3 py-2 text-sm">
                            {isGroup || isClosed || netQty <= 0 ? (
                              <span className="text-gray-500 text-xs">—</span>
                            ) : (
                              <div className="flex items-center gap-2 min-w-[90px]">
                                <div className="flex-1 bg-gray-600 rounded-full h-2 overflow-hidden flex">
                                  <div
                                    className={`h-full ${progressBarColor} transition-all duration-300`}
                                    style={{ width: `${approvedPct}%` }}
                                  />
                                  <div
                                    className="h-full bg-yellow-400/70 transition-all duration-300"
                                    style={{ width: `${pendingPct}%` }}
                                  />
                                </div>
                                <span
                                  className={`text-xs font-medium whitespace-nowrap ${progressColor}`}
                                >
                                  {approvedPct.toFixed(0)}%
                                </span>
                              </div>
                            )}
                          </td>
                        </>
                      ) : (
                        <>
                          {/* Piecework Charged */}
                          <td className="px-3 py-2 text-sm text-right text-green-400">
                            {formatQty(charged)}
                          </td>
                          {/* Piecework Pending */}
                          <td className="px-3 py-2 text-sm text-right text-yellow-400">
                            {formatQty(pendingQty)}
                          </td>
                          {/* Piecework Remaining */}
                          <td className="px-3 py-2 text-sm text-right">
                            {isClosed ? (
                              <span className="text-yellow-600/80 font-medium">
                                Closed
                              </span>
                            ) : remaining > 0 ? (
                              <span className="text-orange-400 font-medium">
                                {formatQty(remaining)}
                              </span>
                            ) : (
                              <span className="text-gray-500">0</span>
                            )}
                          </td>
                        </>
                      )}

                      {/* Back-calculated hours */}
                      {showBackCalculatedHours && (
                        <td className="px-3 py-2 text-sm text-right text-cyan-300 font-medium">
                          {(() => {
                            const effectiveAmount = isGroup
                              ? (item._totalAmount ?? 0)
                              : (item.amount ?? 0);
                            if (effectiveAmount <= 0 || contractHourlyRate <= 0)
                              return "—";
                            return (
                              effectiveAmount / contractHourlyRate
                            ).toFixed(2);
                          })()}
                        </td>
                      )}

                      {/* Amount */}
                      <td className="px-3 py-2 text-sm text-right font-medium text-orange-400">
                        {formatCurrency(
                          isGroup
                            ? (item._totalAmount ?? 0)
                            : (item.amount ?? 0),
                        )}
                      </td>

                      {/* Rate */}
                      <td className="px-3 py-2 text-sm text-right font-medium">
                        <span className="text-white">
                          {formatCurrency(item.unitRate || 0)}
                        </span>
                        {burdenFactor > 1 && (
                          <span className="ml-2 text-blue-300 text-xs">
                            ({formatCurrency(foremanRate(item.unitRate))})
                          </span>
                        )}
                      </td>

                      {wo.type === "piecework" && (
                        <td className="px-3 py-2 text-center">
                          <div className="flex items-center justify-center gap-2">
                            {/* Grouped row: only show Change Rate (no qty variance for groups) */}
                            {isGroup ? (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const groupKey = item.key; // `${itemCode}-${bidArea}-${uom}`
                                  const groupItems =
                                    filteredDisplayItems.filter((fi) => {
                                      const fiDesc =
                                        fi.description ||
                                        fi.costCodeDescription ||
                                        fi.itemDescription ||
                                        "";
                                      const fiCode =
                                        fi.itemCode ||
                                        fiDesc.split(" ")[0] ||
                                        "UNKNOWN";
                                      const fiKey = `${fiCode}-${fi.bidArea || "N/A"}-${fi.uom || "N/A"}`;
                                      return (
                                        fiKey === groupKey &&
                                        !fi.closedOutBySizeChange &&
                                        !fi.isNew
                                      );
                                    });
                                  setBatchRateModal({
                                    isOpen: true,
                                    items: groupItems,
                                    mode: "group",
                                  });
                                }}
                                className="px-2.5 py-1 text-xs font-medium text-purple-300 bg-purple-900/40 border border-purple-700/50 rounded hover:bg-purple-800/60 whitespace-nowrap"
                                title={`Change rate for all ${item._count} items in this group`}
                              >
                                Change Rate ({item._count})
                              </button>
                            ) : (
                              <>
                                {/* Individual variance button — always visible for piecework, non-grouped */}
                                {!item.isNew && (
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setVarianceItem(item);
                                    }}
                                    className="px-2.5 py-1 text-xs font-medium text-purple-300 bg-purple-900/40 border border-purple-700/50 rounded hover:bg-purple-800/60 whitespace-nowrap"
                                    title={
                                      isClosed
                                        ? "Item is closed out"
                                        : "Add quantity delta or change rate"
                                    }
                                  >
                                    Create Variance
                                  </button>
                                )}

                                {/* Remove button — editing mode only */}
                                {editing &&
                                  (item.isNew ? (
                                    <button
                                      type="button"
                                      onClick={() =>
                                        setItemsToAdd((prev) =>
                                          prev.filter(
                                            (a) =>
                                              !(
                                                a.assemblyId ===
                                                  item.assemblyId &&
                                                a.lineItemId === item.lineItemId
                                              ),
                                          ),
                                        )
                                      }
                                      className="text-red-400 hover:text-red-300 text-xs"
                                    >
                                      Remove
                                    </button>
                                  ) : isClosed ? (
                                    <span className="text-gray-500 text-xs">
                                      —
                                    </span>
                                  ) : (
                                    <button
                                      type="button"
                                      onClick={() => {
                                        if (
                                          (item.qtyCharged || 0) > 0 ||
                                          (item.qtyPending || 0) > 0
                                        ) {
                                          setAlert({
                                            open: true,
                                            type: "error",
                                            title: "Cannot Remove",
                                            message: "Item has charges",
                                          });
                                          return;
                                        }
                                        setItemsToRemove((prev) => [
                                          ...prev,
                                          {
                                            assemblyId: item.assemblyId,
                                            lineItemId: item.lineItemId,
                                          },
                                        ]);
                                      }}
                                      disabled={
                                        (item.qtyCharged || 0) > 0 ||
                                        (item.qtyPending || 0) > 0
                                      }
                                      className={`${
                                        (item.qtyCharged || 0) > 0 ||
                                        (item.qtyPending || 0) > 0
                                          ? "text-gray-500 cursor-not-allowed"
                                          : "text-red-400 hover:text-red-300"
                                      } text-xs`}
                                    >
                                      Remove
                                    </button>
                                  ))}
                              </>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                },
              )}

              {/* Totals row */}
              {(groupMainTable ? groupedMainItems : filteredDisplayItems)
                .length > 0 && (
                <>
                  <tr className="bg-gray-800/90 font-bold border-t-2 border-gray-500 sticky bottom-0 z-10">
                    {/* Condition (hidden in group mode) */}
                    {!groupMainTable && <td className="px-3 py-3" />}
                    {/* Section */}
                    <td className="px-3 py-3" />
                    {/* Description */}
                    <td className="px-3 py-3 text-sm text-orange-300 font-bold">
                      TOTALS
                    </td>
                    {/* Bid Area */}
                    <td className="px-3 py-3" />

                    {/* Net required total */}
                    <td className="px-3 py-3 text-right text-orange-400">
                      {formatQty(
                        groupMainTable
                          ? groupedMainItems.reduce(
                              (s, g) => s + (g._totalQuantity ?? 0),
                              0,
                            )
                          : filteredDisplayItems.reduce(
                              (s, i) =>
                                s + (i.netRequiredQty ?? i.quantity ?? 0),
                              0,
                            ),
                      )}
                    </td>

                    {/* UOM — blank */}
                    <td className="px-3 py-3" />

                    {/* Original — blank */}
                    <td className="px-3 py-3" />

                    {/* Delta total */}
                    <td className="px-3 py-3 text-right">
                      {groupMainTable ? (
                        "—"
                      ) : (
                        <span className="text-purple-400">
                          {formatQty(
                            filteredDisplayItems.reduce(
                              (s, i) => s + (i.deltaQty ?? 0),
                              0,
                            ),
                          )}
                        </span>
                      )}
                    </td>

                    {/* Progress totals (hourly) OR charged/pending/remaining (piecework) */}
                    {wo.type === "hourly" && progressStats ? (
                      <>
                        <td className="px-3 py-3 text-right text-green-400 font-bold">
                          {formatCurrency(progressStats.totalApprovedCost)}
                        </td>
                        <td className="px-3 py-3 text-right text-yellow-400 font-bold">
                          {formatCurrency(progressStats.totalPendingCost)}
                        </td>
                        <td className="px-3 py-3 text-right text-orange-300 font-bold">
                          {formatCurrency(progressStats.totalRemainingCost)}
                        </td>
                        <td className="px-3 py-3">
                          {progressStats.totalEstimated > 0 ? (
                            <div className="flex items-center gap-2 min-w-[90px]">
                              <div className="flex-1 bg-gray-600 rounded-full h-2 overflow-hidden flex">
                                <div
                                  className="h-full bg-green-500"
                                  style={{
                                    width: `${progressStats.completionPct}%`,
                                  }}
                                />
                                <div
                                  className="h-full bg-yellow-400/70"
                                  style={{
                                    width: `${progressStats.pendingPct}%`,
                                  }}
                                />
                              </div>
                              <span className="text-xs font-bold text-green-400 whitespace-nowrap">
                                {progressStats.completionPct.toFixed(1)}%
                              </span>
                            </div>
                          ) : (
                            <span className="text-gray-500 text-xs">—</span>
                          )}
                        </td>
                      </>
                    ) : wo.type !== "hourly" ? (
                      <>
                        <td className="px-3 py-3 text-right text-green-400">
                          {formatQty(
                            groupMainTable
                              ? groupedMainItems.reduce(
                                  (s, g) => s + (g._totalCharged ?? 0),
                                  0,
                                )
                              : filteredDisplayItems.reduce(
                                  (s, i) =>
                                    s + (i.qtyOrdered ?? i.qtyCharged ?? 0),
                                  0,
                                ),
                          )}
                        </td>
                        <td className="px-3 py-3 text-right text-yellow-400">
                          {formatQty(
                            groupMainTable
                              ? groupedMainItems.reduce(
                                  (s, g) => s + (g._totalPending ?? 0),
                                  0,
                                )
                              : filteredDisplayItems.reduce(
                                  (s, i) => s + (i.qtyPending ?? 0),
                                  0,
                                ),
                          )}
                        </td>
                        <td className="px-3 py-3 text-right text-orange-400">
                          {formatQty(
                            groupMainTable
                              ? groupedMainItems.reduce(
                                  (s, g) => s + (g._totalRemaining ?? 0),
                                  0,
                                )
                              : filteredDisplayItems.reduce(
                                  (s, i) => s + (i.netRemaining ?? 0),
                                  0,
                                ),
                          )}
                        </td>
                      </>
                    ) : (
                      // hourly, no progress data yet
                      <>
                        <td />
                        <td />
                        <td />
                        <td />
                      </>
                    )}

                    {/* Back-calculated hours */}
                    {showBackCalculatedHours && (
                      <td className="px-3 py-3 text-right text-cyan-400 font-bold">
                        {groupMainTable
                          ? groupedMainItems
                              .reduce((sum, g) => {
                                const a = g._totalAmount ?? 0;
                                return (
                                  sum + (a > 0 ? a / contractHourlyRate : 0)
                                );
                              }, 0)
                              .toFixed(2)
                          : displayItems
                              .reduce((sum, i) => {
                                const a = i.amount ?? 0;
                                return (
                                  sum + (a > 0 ? a / contractHourlyRate : 0)
                                );
                              }, 0)
                              .toFixed(2)}
                      </td>
                    )}

                    {/* Budget amount total */}
                    <td className="px-3 py-3 text-right text-orange-400 font-bold">
                      {formatCurrency(
                        groupMainTable
                          ? groupedMainItems.reduce(
                              (s, g) => s + (g._totalAmount ?? 0),
                              0,
                            )
                          : filteredDisplayItems.reduce(
                              (s, i) => s + (i.amount ?? 0),
                              0,
                            ),
                      )}
                    </td>

                    {/* Rate — blank */}
                    <td className="px-3 py-3" />
                    <td className="px-3 py-3" />
                    {editing && <td className="px-3 py-3" />}
                    <td className="px-3 py-3" />
                  </tr>
                </>
              )}
            </tbody>
          </table>

          {/* Edit save bar */}
          {editing && (
            <div className="mt-6 flex justify-end gap-4 items-center bg-gray-800 p-4 rounded-lg border border-gray-600">
              <div className="mr-auto text-sm">
                {itemsToAdd.length > 0 && (
                  <span className="text-green-400 mr-4">
                    +{itemsToAdd.length} item
                    {itemsToAdd.length !== 1 ? "s" : ""} to add
                  </span>
                )}
                {itemsToRemove.length > 0 && (
                  <span className="text-red-400">
                    -{itemsToRemove.length} item
                    {itemsToRemove.length !== 1 ? "s" : ""} to remove
                  </span>
                )}
                {(itemsToAdd.length > 0 || itemsToRemove.length > 0) && (
                  <span className="text-gray-400 ml-3 italic">
                    (changes not saved yet)
                  </span>
                )}
              </div>
              <button
                onClick={() => setEditing(false)}
                className="px-5 py-2 bg-gray-600 hover:bg-gray-500 text-gray-200 rounded-md"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveChanges}
                disabled={itemsToAdd.length === 0 && itemsToRemove.length === 0}
                className={`px-6 py-2.5 font-medium rounded-md ${itemsToAdd.length === 0 && itemsToRemove.length === 0 ? "bg-gray-600 text-gray-400 cursor-not-allowed" : "bg-green-600 hover:bg-green-700 text-white shadow-lg"}`}
              >
                Save Changes
                {(itemsToAdd.length > 0 || itemsToRemove.length > 0) &&
                  ` (+${itemsToAdd.length - itemsToRemove.length})`}
              </button>
            </div>
          )}
        </div>

        {/* Footer summary bar */}
        <div className="mt-4 px-5 py-3 bg-gray-800 rounded-lg flex flex-wrap justify-between items-center gap-3 border border-gray-700">
          {/* Left: item / filter count */}
          <div className="text-sm text-gray-400">
            <span className="font-medium text-white">
              {groupMainTable
                ? groupedMainItems.length
                : filteredDisplayItems.length}
            </span>{" "}
            {groupMainTable ? "grouped" : "items"}
            {filteredDisplayItems.length !== displayItems.length && (
              <span className="text-gray-600 ml-1">
                / {displayItems.length} total
              </span>
            )}
            {itemsToAdd.length > 0 && (
              <span className="text-green-400 ml-2">
                (+{itemsToAdd.length} pending add)
              </span>
            )}
          </div>

          {/* Right: cost figures only */}
          <div className="flex flex-wrap items-center gap-5">
            {/* Progress cost — hourly only */}
            {wo.type === "hourly" && progressStats && (
              <>
                <div className="flex items-center gap-4 text-sm">
                  <div className="text-right">
                    <div className="text-[10px] text-gray-500 uppercase tracking-wide mb-0.5">
                      Progress cost
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-green-400 font-semibold">
                        {formatCurrency(progressStats.totalApprovedCost)}
                      </span>
                      {progressStats.totalPendingCost > 0 && (
                        <span className="text-[11px] text-yellow-400">
                          +{formatCurrency(progressStats.totalPendingCost)}{" "}
                          pending
                        </span>
                      )}
                    </div>
                  </div>
                  {progressStats.completionPct > 0 && (
                    <div className="text-right">
                      <div className="text-[10px] text-gray-500 uppercase tracking-wide mb-0.5">
                        Complete
                      </div>
                      <div
                        className={`font-bold ${
                          progressStats.completionPct >= 90
                            ? "text-green-400"
                            : progressStats.completionPct >= 50
                              ? "text-blue-400"
                              : "text-yellow-400"
                        }`}
                      >
                        {progressStats.completionPct.toFixed(1)}%
                      </div>
                    </div>
                  )}
                </div>
                <div className="w-px h-8 bg-gray-700" />
              </>
            )}

            {/* Actual cost from timesheets — hourly only */}
            {wo.type === "hourly" && timesheetSummary && (
              <>
                <div className="text-right">
                  <div className="text-[10px] text-gray-500 uppercase tracking-wide mb-0.5">
                    Actual cost
                  </div>
                  <div className="text-sm font-bold text-orange-400">
                    {formatCurrency(timesheetSummary.approved?.totalCost)}
                  </div>
                  {(timesheetSummary.pending?.totalCost || 0) > 0 && (
                    <div className="text-[10px] text-yellow-400">
                      +{formatCurrency(timesheetSummary.pending.totalCost)}{" "}
                      pending
                    </div>
                  )}
                </div>
                <div className="w-px h-8 bg-gray-700" />
              </>
            )}

            {/* Budget total — always */}
            <div className="text-right">
              <div className="text-[10px] text-gray-500 uppercase tracking-wide mb-0.5">
                {filteredDisplayItems.length !== displayItems.length
                  ? "Filtered budget"
                  : "Budget total"}
              </div>
              <div className="text-lg font-bold text-orange-400">
                {formatCurrency(filteredTotals.estimatedTotal)}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
